import { formatClockTime, getTimeFormat } from '@/shared/lib/time-format';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import { isIntakeDeskOn } from '@/lib/intake-switches';
import { ORDER_REQUESTS_TAB_PARAM, initialSchedulingTab, opensOrderRequests } from '@/scheduling/lib/order-requests-return';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import { cn } from '@/lib/utils';
import { CalendarToolbar } from '@/components/blocks/calendar-toolbar';
import { stepPeriod, periodLabel, showsToday, FilterPill, MultiSelectDropdown } from '@/components/blocks/calendar-sub-toolbar';
import { CalendarDateControl } from '@/components/blocks/calendar-date-control';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PracticeSwitcher } from '@/scheduling/components/calendar/practice-switcher';
import { PRACTICE_IN_BAR } from '@/app/layout/practice-in-bar';
import { AppointmentSearch } from '@/scheduling/components/calendar/appointment-search';
import { ProviderStrip, type ProviderSummary } from '@/components/blocks/provider-strip';
import {
  WeekCalendar,
  type CalendarDay,
  type CalendarAppointment,
  type AppointmentDropPayload,
} from '@/components/blocks/week-calendar';
import { DayListCalendar } from '@/components/blocks/day-list-calendar';
import { ByDoctorCalendar, groupRowsByDoctor } from '@/components/blocks/by-doctor-calendar';
import { ByRoomCalendar, groupRowsByRoom } from '@/components/blocks/by-room-calendar';
import { ByDeviceCalendar } from '@/components/blocks/by-device-calendar';
import { SpecialtyFilter, ViewToggleGroup } from '@/components/cards';
import { CalendarActorTabs } from '@/scheduling/components/calendar/calendar-actor-tabs';
import { PractitionerFilterPanel } from '@/scheduling/components/calendar/practitioner-filter-panel';
import { BookDevicePanel } from '@/scheduling/components/devices/book-device-panel';
import { DEVICE_CALENDAR_VIEWS, type CalendarActor } from '@/scheduling/lib/calendar-actor';
import { capSubtypes, DEVICE_SUBTYPE_FILTER_ENABLED } from '@/scheduling/lib/device-columns';
import { MonthCalendar, type MonthCalendarDay, type MonthEvent } from '@/components/blocks/month-calendar';
import { AppointmentListView, type ListAppointment } from '@/components/blocks/appointment-list-view';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import {
  CreateAppointmentPanel,
  type SavedBookingSeed,
} from '@/scheduling/components/appointments/create-appointment-panel';
import {
  BridgeCreateAppointmentModal,
  MODAL_BOOKING,
} from '@/scheduling/components/appointments/create-appointment-modal';
import { InlineEditAppointmentPanel } from '@/scheduling/components/appointments/inline-edit-appointment-panel';
import { BlockAvailabilityPanel } from '@/scheduling/components/block-availability/block-availability-panel';
import {
  BLOCK_ACTOR_DEVICE,
  BLOCK_ACTOR_PRACTITIONER,
  type BlockActorKind,
} from '@/types/block-availability-api';
import { CancelAppointmentDialog } from '@/scheduling/components/appointments/cancel-appointment-dialog';
import { RescheduleAppointmentDialog } from '@/scheduling/components/appointments/reschedule-appointment-dialog';
import { AddNoteDialog } from '@/scheduling/components/appointments/add-note-dialog';
import { PostVisitSummaryDialog } from '@/patients/components/chart/post-visit-summary-dialog';
import { FollowUpRequestsTab } from '@/scheduling/components/fu-requests/follow-up-requests-tab';
import { OrderRequestsTab } from '@/scheduling/components/order-requests/order-requests-tab';
import { ReferralsTab } from '@/scheduling/components/referrals/referrals-tab';
import { useReferralsEnabled } from '@/lib/use-referrals-enabled';
import { TechnicianWorklistTab } from '@/scheduling/components/technician/technician-worklist-tab';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import {
  canSeeOrderRequests,
  canSeeTechnicianWorklist,
  isPhlebotomistRole,
} from '@/auth/roles';
import { confinedTabOf } from '@/app/lib/nav-confinement';
import type {
  CalendarDevice,
  CreateAppointmentProfileRequest,
  DateFilter,
  FuRequestResponse,
  ResourceAvailabilityPeriod,
  SlotResponse,
} from '@/types/scheduling-api';
import { APPOINTMENT_STATUS_CODES, INVOICE_STATUS_OPTIONS } from '@/types/scheduling-api';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { applySpecialityOverride } from '@/scheduling/lib/apply-speciality-override';
import { buildProfileFormValues } from '@/scheduling/lib/build-profile-form-values';
import { buildFollowUpInitialFromRow } from '@/scheduling/lib/build-follow-up-from-row';
import { followUpSeedDateISO } from '@/scheduling/lib/build-follow-up-from-appointment';
import { addMinutesISO } from '@/scheduling/lib/quick-create-slot';
import { promoteChainLeg } from '@/scheduling/ai/promote-chain-leg';
import { useSchedulingAiEnabled } from '@/lib/use-scheduling-ai-enabled';
import { useBookingRailLayout } from '@/lib/use-booking-rail-layout';
import { SchedulingAiCommandBar } from '@/scheduling/ai/scheduling-ai-command-bar';
import { buildAiDraftPrefill } from '@/scheduling/ai/ai-draft-prefill';
import { buildGhostRows, ghostRowsForDay } from '@/scheduling/ai/ghost-rows';
import type { AiFillItem } from '@/scheduling/ai/ai-fill-report';
import type { SchedulingCommandDraft } from '@/scheduling/ai/scheduling-command-stub-parser';
import { ymdLocal } from '@/shared/lib/ymd-local';
import type { OrgLocation } from '@/auth/auth-api';
import type { AppointmentActionId } from '@/scheduling/lib/appointment-actions';

/**
 * FrameScheduling — Frame
 * Top-level composition of the scheduling workspace.
 *
 * Render order (matches Flutter PM):
 *   1. CalendarToolbar — top bar (tabs/search/practice/create) + specialty chips
 *   2. ProviderStrip   — horizontal provider cards
 *   3. CalendarSubToolbar — date picker · status filter pills · view toggle
 *   4. Calendar grid (Day or Week) or AppointmentListView
 */

type SchedulingTab =
  | 'calendar'
  | 'fuRequests'
  | 'orderRequests'
  | 'referrals'
  | 'technicianWorklist';

/**
 * Everything the Devices tab needs (PROD-2007). Supplied by SchedulingPage;
 * when the whole object is absent the frame renders today's practitioner
 * calendar with no actor tabs at all (byte-identical behaviour).
 */
export interface DevicesTabProps {
  actor: CalendarActor;
  onActorChange: (actor: CalendarActor) => void;
  /**
   * The BOOKABLE active devices of the practice — working hours AND at least
   * one product (`isBookableDevice`, narrowed by the page). Feeds the booking
   * picker, so it can only offer what the tab can book (Andrew, 2026-09-13).
   */
  devices: CalendarDevice[];
  /**
   * The BLOCKABLE active devices of the practice — working hours, and that
   * is all (`isBlockableDevice`, narrowed by the page). PROD-2454.
   *
   * A SECOND list rather than a reuse of `devices`, because the two
   * predicates genuinely differ: booking a device needs a product to book,
   * blocking it needs only a window to mark busy. Feeding the block picker
   * the bookable list would withhold a machine that has hours and no product
   * — configurable, in service, and perfectly blockable — from the only
   * control that could take it out of service.
   */
  blockableDevices: CalendarDevice[];
  /**
   * The Devices tab badge (PROD-2364). NOT `devices.length`: that list is
   * empty both for a practice with no bookable devices and for one whose
   * device feeds have not been fetched yet, because they are gated on the tab
   * being active. Reading the length made the inactive Devices tab claim `0`
   * at a practice with five, telling the operator not to bother clicking.
   *
   * `undefined` means "not known yet" and renders no badge at all. Only the
   * page can tell the two empties apart (it holds the query status), so only
   * the page may set this. A real zero still reads `0`.
   */
  deviceCount?: number;
  /**
   * Top cards for the Devices tab (PROD-2184) — the same ProviderSummary the
   * practitioner strip renders, built by `devicesFromDeviceCalendarSlots`.
   * `specialty` carries the technician; `photo` and `issue` are absent.
   */
  cards: ProviderSummary[];
  /** Column set after the subtype filter, already ordered subtype → name. */
  columns: CalendarDevice[];
  rowsByDevice: Map<string, DayListRow[]>;
  availabilityByDevice: Map<string, ResourceAvailabilityPeriod[] | undefined>;
  subtypes: string[];
  selectedSubtypes: string[];
  onSubtypeToggle: (subtype: string) => void;
  onSubtypeClear: () => void;
  isLoading: boolean;
  /** Hour gutter for the device day grid (practice hours ∪ device windows). */
  hours: number[];
  /** Device bookings as week tiles (Week view of the Devices tab). */
  weekAppointments: CalendarAppointment[];
  /** Drag-to-reschedule on a device column — the existing reschedule path. */
  onDeviceAppointmentDrop?: (payload: { id: string; newStartISO: string }) => void;
}

interface FrameSchedulingProps {
  // Data
  providers: ProviderSummary[];
  days: CalendarDay[];
  appointments: CalendarAppointment[];
  /** Day-view rows (table layout) — independent of `appointments` (Week-view tiles). */
  dayListRows?: DayListRow[];
  /** Hour gutter (0-23) for the day-view list. */
  dayListHours?: number[];
  /** Hour to highlight as "now" in the day-view gutter. */
  dayListCurrentHour?: number;
  specialties: string[];
  statusLegend?: { code: string; display: string }[];
  /** Practice locations for the location-filter toolbar buttons. */
  organizationLocations?: OrgLocation[];

  // Date — driven by parent (page reads/writes for API queries)
  selectedDate: Date;
  onSelectedDateChange: (date: Date) => void;

  // Practice context
  practiceName: string;
  organizationId: string;
  practiceId: string;
  userId: string;

  // Counts
  fuRequestCount?: number;

  // List view data
  listAppointments?: ListAppointment[];
  listWaitingRoom?: number;
  listNoShow?: number;
  listCancelled?: number;
  listCurrentTime?: string;

  // Month view (PROD-558) — when activeView=='Month', the parent supplies
  // a pre-built 42-cell grid via the month-mapper. monthLabel reads e.g.
  // "May 2026". Click on a day fires onMonthDayClick → parent typically
  // switches activeView back to Day with that date selected.
  monthGrid?: MonthCalendarDay[];
  monthLabel?: string;
  monthIsLoading?: boolean;
  monthIsError?: boolean;

  /**
   * True while the day-scoped appointments query is in flight. Drives
   * the loading skeleton on ByRoomCalendar (and any other view that
   * surfaces a refetch state). Independent from monthIsLoading.
   */
  dayListIsLoading?: boolean;
  /** The practitioners (AvailableProviders) are still loading: the strip and every view draw skeletons (Antonin, 13 Oct). */
  providersLoading?: boolean;
  /** `?x=calendar-practice-slots`: the practitioner cards' Retry after the calendarSlots request failed. */
  onRetryProviderSlots?: () => void;
  /** The calendarSlots request is running again after a failure (the cards' Retry is busy). */
  providerSlotsRetrying?: boolean;
  /** Whether the clinic books devices is still unknown: hold the Practitioners / Devices row with grey pills so it
   *  doesn't swap in once the setting arrives (the specialty row would show, then vanish). */
  devicesTabPending?: boolean;
  onMonthDayClick?: (iso: string) => void;
  /** Open a specific appointment from a month-cell event card. */
  onMonthEventClick?: (event: MonthEvent) => void;

  // Calendar-view state — optional controlled mode. When parent passes
  // `calendarView` + `onCalendarViewChange`, the frame mirrors the parent
  // (lets SchedulingPage gate the month-range fetch on activeView ==
  // 'Month' to avoid wasted requests in Day/Week mode). Without these
  // props, the frame falls back to its own internal state (defaults to
  // 'Day').
  calendarView?: DateFilter;
  onCalendarViewChange?: (view: DateFilter) => void;

  /**
   * Full practice exam-room list for the By-Room view (PROD-985). Drives
   * the column set so rooms with zero appointments today still render a
   * column. Forwarded to `<ByRoomCalendar rooms={...} />`, which unions it
   * with any appointment-derived rooms. Empty/omitted → columns fall back
   * to the appointment-derived set (pre-PROD-985 behaviour).
   */
  roomColumns?: string[];

  // Callbacks
  onSearchPatients: (query: string) => Promise<Array<{
    id: string;
    firstName: string;
    lastName: string;
    mrn?: string;
    dob?: string;
    phone?: string;
  }>>;
  onAppointmentClick?: (appointmentId: string) => void;
  /**
   * Fires when the user drags an appointment to a new slot in the
   * Week view (PROD-563 drag-drop reschedule). Parent owns the API
   * call + optimistic update via useRescheduleAppointment. Omit to
   * leave the calendar in click-only mode.
   */
  onAppointmentDrop?: (payload: AppointmentDropPayload) => void;
  /**
   * By-Doctor drag-reschedule (PROD-984). Separate from `onAppointmentDrop`
   * (Week) because the By-Doctor flow routes through a confirm dialog at the
   * page level. Forwarded to `ByDoctorCalendar.onAppointmentDrop`. When
   * absent, By-Doctor tiles render click-only (no drag).
   */
  onByDoctorAppointmentDrop?: (payload: { id: string; newStartISO: string }) => void;
  /**
   * Fires when a provider's NAME in the ProviderStrip is clicked — the parent
   * opens that provider's settings page. Flutter parity:
   * `calendar_tab.dart:4215` (name-only tap → DoctorBaseWidget). Omit to leave
   * names as plain text (e.g. when the user lacks `Manage_Doctors` view).
   */
  onProviderNameClick?: (providerId: string) => void;
  /**
   * Devices tab (PROD-2007). Absent → no Practitioners/Devices strip, no
   * device grid, no Book-a-device: exactly the pre-PROD-2007 calendar.
   */
  devicesTab?: DevicesTabProps;
}

import { Clock, Ban, XCircle, Filter, Video, Home, Plus, Receipt } from 'lucide-react';

/** Floating filter pill: a 40 px circle at the right end of the practitioner strip row (8 from its top, 16 from the right
 *  edge, in the lane the strip reserves), so it never covers a grid row, card or button in any view (15 Oct). */
export function FloatingFilterPill({ waitingRoomCount, noShowCount, cancelledCount, statusOptions = [], organizationLocations, className }: {
  waitingRoomCount?: number; noShowCount?: number; cancelledCount?: number;
  /** The Statuses list (/api/Appointment/Status); empty falls back to the status codes. */
  statusOptions?: { code: string; display: string }[];
  organizationLocations: OrgLocation[]; className?: string;
}) {
  const [hoverOpen, setOpen] = useState(false);
  // A Statuses / Invoice statuses list is open: the pill stays expanded under it.
  const [listOpen, setListOpen] = useState(false);
  const open = hoverOpen || listOpen;
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const enter = () => { clearTimeout(timeout.current); setOpen(true); };
  const leave = () => { timeout.current = setTimeout(() => setOpen(false), 300); };

  const selectedStatusCodes = useSchedulingStore((s) => s.selectedStatusCodes);
  const toggleStatusCode = useSchedulingStore((s) => s.toggleStatusCode);
  const selectedLocationIds = useSchedulingStore((s) => s.selectedLocationIds);
  const toggleLocation = useSchedulingStore((s) => s.toggleLocation);
  const setSelectedStatusCodes = useSchedulingStore((s) => s.setSelectedStatusCodes);
  const clearStatusCodes = useSchedulingStore((s) => s.clearStatusCodes);
  const selectedInvoiceStatuses = useSchedulingStore((s) => s.selectedInvoiceStatuses);
  const toggleInvoiceStatus = useSchedulingStore((s) => s.toggleInvoiceStatus);
  const setSelectedInvoiceStatuses = useSchedulingStore((s) => s.setSelectedInvoiceStatuses);
  const clearInvoiceStatuses = useSchedulingStore((s) => s.clearInvoiceStatuses);
  // Saved (persisted) invoice filters count too, so a filter applied in an earlier session is never invisible.
  const activeCount = selectedStatusCodes.length + selectedInvoiceStatuses.length + selectedLocationIds.length;
  const statusList = statusOptions.length > 0
    ? statusOptions
    : APPOINTMENT_STATUS_CODES.map((code) => ({ code, display: code }));

  const LOC_ICONS: Record<string, typeof Plus> = { office: Plus, home: Home, virtual: Video, block: Ban };

  // Under the practitioner hover cards (z 40), above the calendar body (Antonin, 15 Oct: "push filter stuff lower").
  return (
    // Under the practitioner hover cards (z 40), above the calendar body (Antonin, 15 Oct: "push filter stuff lower").
    <div data-cal-filter-pill className={cn('pointer-events-none absolute z-20', className ?? 'right-[var(--cal-filter-right)] top-[var(--cal-filter-top)]')}>
      <div
        className="pointer-events-auto h-[var(--cal-filter-size)] overflow-hidden rounded-full border border-border-default bg-surface-card/90 shadow-sm backdrop-blur-sm transition-[max-width] duration-200 ease-out"
        style={{ maxWidth: open ? 896 : activeCount > 0 ? 56 : 40 }}
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        <div className="flex h-full items-center gap-2 whitespace-nowrap px-3">
          <Filter className="h-4 w-4 shrink-0 text-text-secondary" />
          {activeCount > 0 && <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-brand-primary px-1 text-size-2xs font-semibold text-text-inverse">{activeCount}</span>}
          <FilterPill icon={Clock} label="Waiting" count={waitingRoomCount} active={selectedStatusCodes.includes('CheckedIn')} onClick={() => toggleStatusCode('CheckedIn')} />
          <FilterPill icon={Ban} label="No show" count={noShowCount} active={selectedStatusCodes.includes('NoShow')} onClick={() => toggleStatusCode('NoShow')} />
          <FilterPill icon={XCircle} label="Cancelled" count={cancelledCount} active={selectedStatusCodes.includes('Cancelled')} onClick={() => toggleStatusCode('Cancelled')} />
          {/* Every status + the invoice statuses, the same store sets the pills above edit (main's sub-toolbar dropdowns). */}
          <MultiSelectDropdown icon={Filter} label="Statuses" active={selectedStatusCodes.length > 0}
            options={statusList.map((st) => ({ value: st.code, label: st.display }))} selected={selectedStatusCodes}
            onToggle={toggleStatusCode} onSelectAll={() => setSelectedStatusCodes(statusList.map((st) => st.code))}
            onClearAll={clearStatusCodes} onOpenChange={setListOpen} />
          <MultiSelectDropdown icon={Receipt} label="Invoice statuses" active={selectedInvoiceStatuses.length > 0}
            options={INVOICE_STATUS_OPTIONS.map((v) => ({ value: v, label: v }))} selected={selectedInvoiceStatuses}
            onToggle={toggleInvoiceStatus} onSelectAll={() => setSelectedInvoiceStatuses([...INVOICE_STATUS_OPTIONS])}
            onClearAll={clearInvoiceStatuses} onOpenChange={setListOpen} />
          {organizationLocations.map((loc) => {
            const BridgeIcon = LOC_ICONS[loc.name.toLowerCase()] ?? Plus;
            const active = selectedLocationIds.includes(loc.id);
            return (
              <button key={loc.id} type="button" onClick={() => toggleLocation(loc.id)}
                className={cn('flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-[length:var(--type-meta-size)] font-medium transition-colors', active ? 'bg-brand-primary text-text-inverse' : 'text-text-secondary hover:text-text-primary')}>
                <BridgeIcon className="h-3 w-3" />{loc.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function FrameScheduling({
  providers,
  days,
  appointments,
  dayListRows = [],
  dayListHours = [],
  dayListCurrentHour,
  specialties,
  statusLegend = [],
  organizationLocations = [],
  selectedDate,
  onSelectedDateChange,
  practiceName,
  organizationId,
  practiceId,
  userId,
  fuRequestCount = 0,
  listAppointments = [],
  listWaitingRoom = 0,
  listNoShow = 0,
  listCancelled = 0,
  listCurrentTime,
  monthGrid = [],
  monthLabel = '',
  monthIsLoading = false,
  monthIsError = false,
  roomColumns = [],
  dayListIsLoading = false,
  providersLoading = false,
  onRetryProviderSlots,
  providerSlotsRetrying = false,
  devicesTabPending = false,
  onMonthDayClick,
  onMonthEventClick,
  calendarView: controlledCalendarView,
  onCalendarViewChange,
  onSearchPatients,
  onAppointmentClick,
  onAppointmentDrop,
  onByDoctorAppointmentDrop,
  onProviderNameClick,
  devicesTab,
}: FrameSchedulingProps) {
  // Devices tab (PROD-2007). `devicesActive` gates every device-only branch
  // below; with `devicesTab` absent or on the Practitioners tab, nothing here
  // changes what the practitioner calendar renders.
  // Derived from the `?actor=` URL param, independently of `activeTab` —
  // which used to be a hole (PROD-2398): `/?actor=devices` made
  // `onBookDeviceClick` truthy and so rendered the toolbar's Book Device
  // button for a confined Technician, who has no other control on the
  // screen. Closed on both sides now: `confinedRedirectPath` strips the
  // calendar deep-link params off `/` for a confined session, and the
  // button itself is gated on `!confinedToWorklist` below. The button gate
  // is the load-bearing one; the strip is hygiene.
  //
  // The reasoning that did NOT survive review: "low risk, the options behind
  // it are permission-gated to false." Permissions are asserted in the
  // browser and nowhere else — PROD-2388 — so that was never a control.
  const devicesActive = devicesTab?.actor === 'devices';
  // With the Practitioners / Devices bar, the practitioner filters sit behind the Practitioners button (13 Oct).
  const integratedFilters = !!devicesTab && !devicesActive;
  const holdActorRow = !devicesTab && devicesTabPending;
  /**
   * Book-a-device request. `seq` remounts the rail per open so the initial
   * device / start props are read fresh (no state-in-effect). The OPENER
   * lives further down, next to `startManualFlow` — opening a device booking
   * takes the single rail slot, so it has to be able to tear down what was
   * in it (PROD-2186).
   */
  const [bookDevice, setBookDevice] = useState<{
    deviceId: string | null;
    startISO: string | null;
    seq: number;
  } | null>(null);
  const deviceRows = useMemo(
    () => (devicesTab ? [...devicesTab.rowsByDevice.values()].flat() : []),
    [devicesTab],
  );
  /**
   * Experiment (Antonin, 14 Oct): central booking modal — used for flows
   * triggered from the appointment popup (Follow-up from the popup, FU
   * Requests tab). `props` carries the initialValues and context to forward
   * to `BridgeCreateAppointmentModal`. Only active when `MODAL_BOOKING` is true.
   */
  const [modalBooking, setModalBooking] = useState<{
    initialValues?: Partial<import('@/types/appointment-form').AppointmentFormValues>;
  } | null>(null);
  /** Expanded state of the capped subtype-chip row (review #4). */
  const [showAllSubtypes, setShowAllSubtypes] = useState(false);
  const visibleSubtypes = useMemo(
    () =>
      capSubtypes(
        devicesTab?.subtypes ?? [],
        devicesTab?.selectedSubtypes ?? [],
        showAllSubtypes ? Number.POSITIVE_INFINITY : undefined,
      ),
    [devicesTab?.subtypes, devicesTab?.selectedSubtypes, showAllSubtypes],
  );
  // Single source of truth for the inline left rail (Samer review §3).
  // Two separate booleans (showCreate / showBlock) raced on fast
  // double-clicks — both panels could mount briefly between renders.
  // One enum value can only resolve to one panel.
  // PROD-2186 added 'device': booking a device is a RAIL now, not a dialog,
  // so it takes the same ONE slot and joins the same invariant. Two rails on
  // screen at once is unrepresentable rather than merely unlikely.
  type ActivePanel = 'appointment' | 'block' | 'device' | null;
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  /**
   * Block id when the panel is open for an existing block (edit mode).
   * Null when the panel is open for create (or closed). Mirrors Flutter
   * `BlockAvailabilityWidget.id` — when set, the panel loads the
   * existing block via `GET /api/Appointment/BlockAppointment?id=...`
   * and renders in edit mode.
   */
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  /**
   * WHICH kind of actor the block panel is open for (PROD-2454).
   *
   * Kept HERE rather than derived from `devicesActive` at render time,
   * because the two can disagree for one render: closing the panel and
   * switching tab are separate state updates, and reading the tab would let
   * an open practitioner panel silently become a device panel — hydrated
   * state and all — the instant the tab flipped underneath it. Every entry
   * point sets this in the same breath as `setActivePanel('block')`.
   */
  const [blockActorKind, setBlockActorKind] = useState<BlockActorKind>(
    BLOCK_ACTOR_PRACTITIONER,
  );
  /**
   * Bumped on every open of the Block rail. Together with `blockActorKind` it
   * is that rail's `key`, so each open CONSTRUCTS the panel instead of reusing
   * the one from last time — the same thing `bookDevice.seq` does for the
   * Book-a-device rail, and for the same reason: the panel's actor kind and
   * start date are constructor arguments with no setters (deliberately — a
   * mid-form kind switch would have to invalidate the actor, the shift list and
   * both times), and a constructor that never runs again reads nothing.
   *
   * PROD-2515 is what happens without it. `BlockAvailabilityPanel` lives here
   * and stays mounted for the session, so it initialised ONCE, before any entry
   * point had run, as a practitioner panel on today's date. Choosing "Block a
   * device" set `blockActorKind` above and the panel never read it again: the
   * actor was the machine, the kind was still `practitioner`, `useActorShifts`
   * took the presence branch, and `GET /api/Doctor/Presence` was called with a
   * device id — 500, twice, while `ResourceAvailability` was never called at
   * all. The rail showed "No Slots Available" and a device could not be blocked
   * at all. The stale date came from the same single construction: the rail
   * opened on today while the calendar behind it was on 1 October.
   *
   * The kind is in the key as well as the seq, even though every opener sets
   * both. The seq alone would work today; the kind makes the invariant
   * STRUCTURAL, so a future entry point that sets the kind and forgets the bump
   * cannot quietly bring the bug back.
   */
  const [blockOpenSeq, setBlockOpenSeq] = useState(0);
  /**
   * Pre-filled form values for CreateAppointmentPanel (PROD-829
   * quick-create). Null = open in fresh-create mode (legacy toolbar
   * Create button). Populated from the unified `handleQuickCreate`
   * below — built when the user clicks a slot or appointment-profile
   * chip on the provider strip.
   */
  const [pendingInitialValues, setPendingInitialValues] = useState<
    Partial<AppointmentFormValues> | null
  >(null);
  const showCreate = activePanel === 'appointment';
  const showBlock = activePanel === 'block';
  const showDevice = activePanel === 'device';

  // ── Scheduling-AI (VITE_SCHEDULING_AI, fail-closed) ────────────────────
  // Flag off → `schedulingAiEnabled === false` and literally nothing below
  // renders or fires: no command bar (toolbar slot stays undefined), no
  // ghost rows (empty array), no alternatives fetch (the bar owns it and
  // is never mounted), no patient-search seed (aiDraft stays null).
  const schedulingAiEnabled = useSchedulingAiEnabled();
  /** Active AI draft — drives ghost rows + the rail's patient-search seed.
   *  Cleared when the create rail closes/discards (single lifecycle). */
  const [aiDraft, setAiDraft] = useState<SchedulingCommandDraft | null>(null);
  // A draft placed on the calendar waits on the user: the floating Scrips Intelligence button glows steadily (13 Oct).
  /** Prompt-filled vs needs-you checklist for the rail (ai-fill-report.ts). */
  const [aiFillReport, setAiFillReport] = useState<AiFillItem[] | null>(null);
  /** Backend rejected the drafted day (PROD-1846 live verdict) → amber ghosts. */
  const [aiRejected, setAiRejected] = useState(false);

  /** Book-time slot rejection (audit #8): the backend's atomic claim
   *  guard said the picked slot is gone — the rail's ConflictCard is the
   *  recovery surface. Cleared with the draft and on every new draft. */
  const [aiBookConflict, setAiBookConflict] = useState(false);

  const clearAiDraft = () => {
    setAiDraft(null);
    setAiRejected(false);
    setAiFillReport(null);
    setAiBookConflict(false);
  };

  /** Bumped whenever the user starts a MANUAL booking flow — the bar drops
   *  any parse still in flight from before the bump (audit #2: a late
   *  parse used to remount the form and destroy typed manual work). */
  const [aiSuspendSeq, setAiSuspendSeq] = useState(0);
  const startManualFlow = () => {
    clearAiDraft();
    setAiSuspendSeq((s) => s + 1);
  };

  /**
   * Open the Book-a-device RAIL (PROD-2186).
   *
   * Andrew, 2026-09-13: "create device became popup which is wrong" … "when
   * pressing create, the panel should appear as the practitioner one exactly".
   * As a dialog this could float over an open create rail; as a rail it takes
   * the slot, so opening one must close whatever held it — the same teardown
   * inline-edit does when IT takes the rail: drop any in-flight AI parse and
   * its draft (a device booking is a manual flow), drop the create rail's
   * prefill so it cannot silently remount with stale values, and drop the
   * block panel's edit target. `setActivePanel('device')` then does the
   * closing itself: one enum can only resolve to one rail.
   */
  const openBookDevice = (deviceId: string | null, startISO: string | null) => {
    startManualFlow();
    setPendingInitialValues(null);
    setEditingBlockId(null);
    setActivePanel('device');
    setBookDevice((cur) => ({ deviceId, startISO, seq: (cur?.seq ?? 0) + 1 }));
  };

  /**
   * Open the Block rail — the ONE place that does it (PROD-2515).
   *
   * Every entry point has to set the same four things together: the edit
   * target, the actor kind, the remount seq and the rail slot, plus the manual
   * -flow teardown every rail open owes. They were written out four times, and
   * two of them were already wrong — the month grid's block-tile click never
   * set the kind at all, so a practitioner block opened there inherited
   * whichever kind was last used. Stating it once is the fix for that as much
   * as for the bump.
   *
   * `toggle` is the difference between the two shapes of opener and nothing
   * else: a Create-menu item toggles the rail it owns, a click on an existing
   * block tile opens it outright.
   */
  const openBlockPanel = (
    kind: BlockActorKind,
    opts?: { blockId?: string | null; toggle?: boolean },
  ) => {
    setEditingBlockId(opts?.blockId ?? null);
    setBlockActorKind(kind);
    setBlockOpenSeq((s) => s + 1);
    // Same lifecycle rule as every other rail open (audits #3 + #2): drop the
    // AI draft and suspend in-flight parses, or a late parse remounts over
    // manual work.
    startManualFlow();
    setActivePanel((cur) => (opts?.toggle && cur === 'block' ? null : 'block'));
  };

  /**
   * The command bar's × — FULL undo of the AI request (Codex P1 on
   * 1dd2c6e7): drop the draft/ghosts AND close the rail the request
   * opened, clearing its prefill — otherwise the bar looks reset while
   * the mistaken appointment stays prefilled in the side panel. Guarded
   * on an active AI draft so a manually-opened rail is never torn down.
   * The rail header's × (onAiDismiss → clearAiDraft) stays the lighter
   * intent: drop the AI layer, keep the rail as it stands.
   */
  const undoAiRequest = () => {
    if (aiDraft) {
      setActivePanel(null);
      setPendingInitialValues(null);
    }
    clearAiDraft();
  };

  /**
   * Command-bar submit/accept → open the EXISTING create rail via the
   * same prefill path quick-create uses (`pendingInitialValues` +
   * `setActivePanel('appointment')`). The AI never books — it seeds
   * selectedDate + recurrence and the patient SEARCH text; provider,
   * profile, real slot, and Save all stay in the rail's own flow.
   */
  /**
   * Advance the back-to-back chain after a successful save (Codex review on
   * #836: the UI promised "book each in turn" but a save cleared the whole
   * draft). Pops the next leg into a fresh draft — same patient, same day,
   * the leg's own profile/doctor — and re-opens the rail for it.
   */
  /** Set by advanceAiChain so the panel's post-save onClose doesn't clear the
   *  freshly advanced leg (the panel fires onSaveSuccess THEN onClose). */
  const aiChainAdvancedRef = useRef(false);

  const advanceAiChain = (saved?: SavedBookingSeed) => {
    // Pure transform extracted to promote-chain-leg.ts (audit test 3) —
    // the frame keeps only the choreography: mark the advance so the
    // post-save onClose doesn't tear down the fresh leg, then re-draft.
    const legDraft = aiDraft ? promoteChainLeg(aiDraft, saved) : null;
    if (!legDraft) return;
    aiChainAdvancedRef.current = true;
    handleAiDraft(legDraft);
  };

  const handleAiDraft = (draft: SchedulingCommandDraft) => {
    setAiBookConflict(false); // a fresh draft starts without a book-time verdict
    // Doctor resolution ladder: the LIVE intent lane (PROD-1316) returns a
    // roster id directly — trust it (ids can only come from the candidate
    // list we sent). Otherwise "with <doctor>" free text resolves by fuzzy
    // name match (every uttered word must appear in the provider's name,
    // case-insensitive). No match → provider stays unset and the rail's own
    // picker is the fallback, exactly as before.
    const doctorWords = draft.doctorQuery?.toLowerCase().split(/\s+/) ?? [];
    const resolvedProvider = draft.practitionerId
      ? providers.find((p) => p.id === draft.practitionerId)
      : doctorWords.length > 0
        ? providers.find((p) => {
            const name = p.name.toLowerCase();
            return doctorWords.every((w) => name.includes(w));
          })
        : undefined;
    // Visit profile: same ladder — live-lane id first, then "follow up"
    // free text against the resolved provider's quick-book profile set
    // (profiles are per-provider, so only theirs are searched).
    const providerProfiles = (resolvedProvider?.quickBookGroups ?? []).flatMap((g) => g.profiles);
    const profileWords = draft.profileQuery?.toLowerCase().split(/\s+/) ?? [];
    const resolvedProfile = draft.profileId
      ? providerProfiles.find((pr) => pr.id === draft.profileId)
      : resolvedProvider && profileWords.length > 0
        ? providerProfiles.find((pr) => {
            const n = (pr.profileName ?? '').toLowerCase();
            return n.length > 0 && profileWords.every((w) => n.includes(w));
          })
        : undefined;

    // The rail labels its checked rows from the draft's query words. Live-lane
    // drafts resolved by ID often carry no words ("Doctor — selected") — enrich
    // the stored draft with the resolved display names so the rail reads
    // "Doctor — Rose Logan", not a placeholder.
    setAiDraft({
      ...draft,
      ...(resolvedProvider && !draft.doctorQuery ? { doctorQuery: resolvedProvider.name } : {}),
      ...(resolvedProfile?.profileName && !draft.profileQuery
        ? { profileQuery: resolvedProfile.profileName }
        : {}),
    });

    const profileValues = resolvedProfile
      ? (() => {
          const { providerSpeciality, providerSpecialityCode } = applySpecialityOverride(
            resolvedProfile,
            { providerSpecialityDisplay: resolvedProvider?.specialty ?? '' },
          );
          return {
            ...buildProfileFormValues(resolvedProfile),
            providerSpeciality,
            providerSpecialityCode,
          };
        })()
      : {};

    setPendingInitialValues({
      ...buildAiDraftPrefill(draft),
      // Human-confirmed patient riding a chain advance (audit #4): the
      // form starts with the patient already selected instead of
      // re-searching the name the user confirmed one leg ago.
      ...(draft.patientId
        ? { patientId: draft.patientId, patientName: draft.patientQuery }
        : {}),
      // Same field trio quick-create seeds — providerId alone renders the
      // rail's provider box as the "?" initials fallback.
      ...(resolvedProvider
        ? {
            providerId: resolvedProvider.id,
            providerName: resolvedProvider.name,
            providerSpeciality: resolvedProvider.specialty,
          }
        : {}),
      ...profileValues,
    });

    // The visible prompt-filled vs needs-you seam (Samer 2026-08-11).
    const fmtWhen = (() => {
      if (!draft.start) return '';
      const [dpart, tpart = '00:00'] = draft.start.split('T');
      const [y, m, dd] = dpart.split('-').map(Number);
      const [hh, mm] = tpart.split(':').map(Number);
      const dt = new Date(y, (m ?? 1) - 1, dd ?? 1, hh ?? 0, mm ?? 0);
      // 24-hour "10:00"; the 12-hour preference keeps this line's "10:00am".
      const time = getTimeFormat() === '12h' ? `${formatClockTime(dt, '12h')}m` : formatClockTime(dt, '24h');
      return `${dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · ${time}`;
    })();
    const report: AiFillItem[] = [
      { key: 'patient', label: `Patient — ${draft.patientQuery}`, state: 'filled' },
      resolvedProvider
        ? { key: 'doctor', label: `Doctor — ${resolvedProvider.name}`, state: 'filled' }
        : {
            key: 'doctor',
            label: draft.doctorQuery
              ? `Doctor "${draft.doctorQuery}" not found — pick one`
              : 'Doctor — pick one',
            state: 'needs-you',
            view: 'provider-selection',
          },
      resolvedProfile
        ? { key: 'profile', label: `Visit — ${resolvedProfile.profileName || draft.profileQuery}`, state: 'filled' }
        : {
            key: 'profile',
            label: draft.profileQuery
              ? `Visit type "${draft.profileQuery}" — pick below`
              : 'Visit type — pick below',
            state: 'needs-you',
          },
      ...(draft.recurrence
        ? [
            {
              key: 'recurrence',
              label: `Repeats ${draft.recurrence.period === 'Months' ? 'monthly' : 'weekly'} ×${draft.recurrence.count} — confirm the dates`,
              state: 'needs-you' as const,
              view: 'recurrence-config' as const,
            },
          ]
        : fmtWhen
          ? [{ key: 'when', label: `When — ${fmtWhen}`, state: 'filled' as const }]
          : []),
      ...(draft.chain && draft.chain.length > 0
        ? [
            {
              key: 'chain',
              label: `${draft.chain.length + 1} back-to-back visits — book each in turn`,
              state: 'needs-you' as const,
            },
          ]
        : []),
    ];
    setAiFillReport(report);

    setActivePanel('appointment');
    // Land the calendar on the drafted day so the ghost rows are in view.
    if (draft.start) onSelectedDateChange(new Date(draft.start));
  };

  /** Ghost occurrences for the day being shown (Day list only). */
  const aiGhostRows = useMemo(() => {
    if (!schedulingAiEnabled || !aiDraft) return [];
    return ghostRowsForDay(buildGhostRows(aiDraft, aiRejected), selectedDate);
  }, [schedulingAiEnabled, aiDraft, aiRejected, selectedDate]);

  /**
   * Unified quick-create handler — wires the three Calendar provider-strip
   * trigger paths (PROD-829, Flutter `calendar_screen.dart:307-410`
   * `onQuickCreate`):
   *
   *   - Path A (popover slot) — slot + preferred profile + provider
   *   - Path B ("Next" inline slot) — slot + preferred profile + provider
   *   - Path C (profile chip) — null slot + specific profile + provider
   *
   * Speciality override applies to ALL three paths (Samer scope
   * 2026-05-19): the provider's speciality used in the pre-fill
   * comes from the chosen profile's speciality with fallback to the
   * provider's own. See `apply-speciality-override.ts`.
   */
  const handleQuickCreate = (
    providerId: string,
    profile: CreateAppointmentProfileRequest,
    slot: SlotResponse | null,
    // The hover card's location buttons: keep only the location; the user picks the profile (Antonin, 15 Oct).
    { locationOnly = false }: { locationOnly?: boolean } = {},
  ) => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider) return;
    const { providerSpeciality: overrideDisplay, providerSpecialityCode } =
      applySpecialityOverride(profile, {
        providerSpecialityDisplay: provider.specialty,
      });

    // Build the profile slice (profileName, color, duration,
    // appointmentType, reasonCode, location, pickedProfile) using the
    // same logic the in-panel `selectProfile()` runs — without it,
    // the panel renders the profile card empty (no name, no
    // duration, no color). Async fields (reminder + billing) come
    // from `usePreBookAppointmentProfile` fired below.
    const builtProfile = buildProfileFormValues(profile);
    const profileValues = locationOnly ? { location: builtProfile.location } : builtProfile;

    // Manual quick-create must not inherit a stale AI draft's checklist/
    // ghosts/patient seed (Codex review on #836) — and it suspends any
    // in-flight parse (audit #2).
    startManualFlow();
    setPendingInitialValues({
      providerId,
      providerName: provider.name,
      providerSpeciality: overrideDisplay,
      providerSpecialityCode,
      ...profileValues,
      ...(slot
        ? {
            slotId: `${providerId}-${slot.start}`,
            slotStart: slot.start,
            slotEnd: slot.end,
          }
        : {
            slotId: null,
            slotStart: null,
            slotEnd: null,
          }),
      // Pass the calendar's currently-selected date through so the
      // panel's date strip lands on the right day (Flutter `:329`).
      // `selectedDate` is a local-midnight Date, so it MUST be reduced with
      // local components. `toISOString().slice(0,10)` converted to UTC first
      // and landed a day early for the whole business day in Asia/Dubai
      // (UTC+4) — picking Aug 26 showed Aug 25 on the slot cards, and the
      // slot query then genuinely fetched the previous day.
      selectedDate: (slot?.start
        ? slot.start.slice(0, 10)
        : ymdLocal(selectedDate)),
    } as Partial<AppointmentFormValues>);

    setActivePanel('appointment');
    // The PreBook chain (reminders / billing / reasonOptions) fires
    // from inside `appointment-profile-section.tsx` via its hydration
    // effect — single source of truth for that side-effect, prevents
    // double-firing.
  };

  /**
   * Empty-cell quick-create from the By-Doctor grid (PROD-983). Flutter
   * parity: `calendar_by_doctor_widget.dart:499-530` (double-tap empty
   * cell) → `calendar_tab.dart:537-566` — pick the provider's
   * preferred-or-first profile, set `slotEnd = slotStart +
   * profile.minutesDuration`, and open the create panel pre-filled. We
   * reuse `handleQuickCreate` (the same path the provider-strip chips use)
   * by synthesising the slot from the clicked start time + profile
   * duration. By-Doctor only — Week/Room have no equivalent in Flutter
   * (confirmed 2026-06-03).
   *
   * Note on the working-hours guard (PROD-983 review §4 — see
   * docs/CONVERSIONS/prod-983-quick-create-slot/VERIFICATION_REPORT.md
   * "Known divergence"): Flutter pre-checks
   * `calendarActionValidationOnWorkingHours` before opening. ProviderSummary
   * doesn't expose structured per-provider working-hours bounds to the grid,
   * but the grid's visible hour range is already derived from working hours
   * (`hoursForDayList`), and — critically — the create panel cannot be
   * submitted before slot availability resolves: the Zod schema's superRefine
   * requires `slotId` (`appointment-form.ts:418-422`), which is only set when
   * the user picks a slot from the fetched available-slots list, so the
   * panel's slot-availability fetch is the authoritative guard (an
   * out-of-hours start simply yields no bookable slot — no race).
   */
  const handleGridQuickCreate = (doctorId: string, startISO: string) => {
    const provider = providers.find((p) => p.id === doctorId);
    if (!provider) return;
    // Preferred-or-first profile, precomputed from the provider's RAW
    // original-order appointmentProfile list (review §2) — NOT the re-sorted
    // quickBookGroups. Matches Flutter's selection exactly.
    const profile = provider.preferredProfile;
    if (!profile) return; // provider has no configured profiles → no-op
    const endISO = addMinutesISO(startISO, profile.minutesDuration ?? 0);
    handleQuickCreate(doctorId, profile, {
      start: startISO,
      end: endISO,
    } as SlotResponse);
  };

  /**
   * Route appointment clicks. Block tiles open the BlockAvailabilityPanel
   * in edit mode (Flutter `:498-577` — the same widget renders edit when
   * `widget.id != null`). Regular appointments propagate to the caller's
   * `onAppointmentClick` (which today opens ViewAppointmentPanel).
   */
  const handleAppointmentClick = (rowId: string) => {
    const row = (devicesActive ? deviceRows : dayListRows).find((r) => r.id === rowId);
    if (row?.isBlock) {
      if (!canEditBlock) {
        toast.error('You are not authorized to edit blocked availability.');
        return;
      }
      // A block tile clicked on the Devices tab is a DEVICE block — the row
      // came out of `rowsByDevice`, which only holds rows carrying a device
      // participant. The record itself cannot tell us (PROD-2454: the block
      // knows an actor id and nothing about its type), so the column the
      // user clicked is the evidence. Since PROD-2512 the kind also picks the
      // hydration ROUTE, and each route refuses the other's actor kind, so
      // getting this wrong is now a 400 rather than a mislabelled card.
      openBlockPanel(devicesActive ? BLOCK_ACTOR_DEVICE : BLOCK_ACTOR_PRACTITIONER, {
        blockId: rowId,
      });
      return;
    }
    onAppointmentClick?.(rowId);
  };

  /**
   * PROD-920 — open `CreateAppointmentPanel` pre-populated from an FU
   * Request row. Mirrors Flutter's `createFollowUpAppointment` callback
   * (`fu_requests_tab.dart:35,41,469-473`) — the parent receives the
   * picked row, derives initial values, and routes to the appointment
   * creation flow. Permission gate (`Doctor_Calender` add) is enforced
   * inside the FU tab; here we trust the callback only fires for
   * authorised users.
   *
   * Field mapping is intentionally minimal (patient + provider basics)
   * — the user picks date/time/profile/etc. in the panel. The PreBook
   * chain (reminders / billing / reasonOptions) fires from the
   * profile-section hydration effect once a profile is chosen, same
   * as the quick-create path above.
   */
  const handleFuCreateFollowUp = (row: FuRequestResponse) => {
    // Flutter `calendar_screen.dart:180`:
    //   `location: widget.userData!.organizationLocations!.first`
    // Pre-set ONLY when the first org location actually has a real
    // GUID — otherwise leave `form.location` null so
    // `LocationSelector` can do its own auto-pick from
    // `useOrgLocations(orgId)` (a separate API call that's more
    // reliable than `user.organizationLocations` for some sessions).
    // Setting `{id:"", name:""}` here would be TRUTHY and cause
    // LocationSelector's `if (!selected)` guard to skip — landing the
    // user on a non-pickable empty Guid that 400s on Save.
    const userLocations =
      useAuthStore.getState().user?.organizationLocations ?? [];
    const firstLocation = userLocations[0]?.id ? userLocations[0] : null;
    const initial: Partial<AppointmentFormValues> = {
      // First org location — Flutter `calendar_screen.dart:180`.
      // The profile picker will narrow profiles to this location;
      // the LocationSelector inside the panel still lets the user
      // switch.
      location: firstLocation
        ? {
            id: firstLocation.id ?? '',
            name: firstLocation.name ?? '',
          }
        : null,
      providerId: row.provider?.id ?? null,
      providerName: row.provider?.name ?? null,
      providerSpeciality: specialityDisplay(row.provider?.speciality) ?? null,
      patientId: row.patient?.id ?? null,
      patientName:
        row.patient?.name?.trim() ||
        [row.patient?.firstName, row.patient?.middleName, row.patient?.lastName]
          .map((s) => s?.trim() ?? '')
          .filter((s) => s.length > 0)
          .join(' ') ||
        null,
      patientPhoto: row.patient?.photoUrl ?? null,
      patientDob: row.patient?.dob ?? null,
      patientGender: row.patient?.gender ?? null,
      // Active RFV for the NEW appointment — Flutter
      // `calendar_screen.dart:170` `reasonCode: request.reasonCode`.
      // Carries the FU request's reason forward as the seed value;
      // gets overwritten if the user picks a profile that bundles its
      // own reasonCode, but until then the panel renders the RFV
      // chip pre-populated.
      reasonCode: row.reasonCode
        ? {
            code: row.reasonCode.code ?? '',
            display: row.reasonCode.display ?? '',
            definition: row.reasonCode.definition ?? '',
          }
        : null,
      // Original appointment id — drives the Previous Appointment
      // section AND signals the appointment-profile picker to filter
      // to FOLLOWUP-type profiles only (downstream of this flag, see
      // `appointment-profile-section.tsx`). Mirrors Flutter
      // `calendar_screen.dart:178` `referenceAppointmentId`.
      //
      // Flutter parity note (deliberate): Flutter uses a SEPARATE
      // `appointment.selectedAppointmentType` STRING field (set to
      // `appointmentTypeFollowUp = "FOLLOWUP"` at
      // `calendar_screen.dart:182`) as the profile-picker lock, and
      // leaves `appointment.appointmentType` (the Coding object) null
      // until the user picks a profile. React collapses these two
      // into one signal: `referenceAppointmentId` is set ⇔
      // FOLLOWUP flow ⇔ profile filter active. The Coding-object
      // `appointmentType` lands naturally when the user picks a
      // FOLLOWUP profile (the only kind shown). This satisfies the
      // schema's FOLLOWUP-requires-referenceAppointmentId rule
      // (appointment-form.ts:357-364) without pre-setting an
      // appointmentType that Flutter never pre-sets.
      referenceAppointmentId: row.appointmentId ?? null,
      // FU request id — drives the wire `followUpId` field on save
      // (`transform-form-to-request.ts:303`). Mirrors Flutter
      // `calendar_screen.dart:179`: `followUpId: request.id`. Distinct
      // from `referenceAppointmentId` (the ORIGINAL appointment's id);
      // both ship on FU saves.
      followUpId: row.id ?? null,
      // Previous Appointment section — display fields cached so the
      // "selected" card renders without an extra fetch. Mirrors
      // Flutter `calendar_screen.dart:183-185,189`.
      previousAppointmentRFV: row.reasonCode?.display ?? null,
      previousAppointmentLocation: row.location
        ? {
            id: row.location.id ?? undefined,
            name: row.location.name ?? undefined,
          }
        : null,
      previousAppointmentDate: row.createdOn ?? null,
      // Flutter: `isPreviousAppointmentDeletable: false` — hides the ×
      // on the Previous Appointment card when seeded from an FU
      // request (the link is structural, not optional).
      previousAppointmentDeletable: false,
    };
    if (MODAL_BOOKING) {
      // Experiment (Antonin, 14 Oct): FU Requests tab follow-up opens the
      // central modal instead of the inline side panel.
      setModalBooking({ initialValues: initial });
    } else {
      startManualFlow(); // manual FU-seeded open — clears the draft AND suspends in-flight parses (audit #2)
      setPendingInitialValues(initial);
      setActivePanel('appointment');
    }
  };
  // Role gate for the Lab linkage tab — visible to Phlebotomists
  // (`tenantRoleId === '19'`, Flutter `phlebotomistTenantRole` at
  // app_constants.dart:725) AND to organisation administrators
  // (`role === '1'`, Flutter `orgAdminRole` at app_constants.dart:722).
  // OrgAdmins were added 2026-05-21 per Andrew so practice admins can
  // operate the Phlebotomist surface without needing a dedicated
  // Phlebotomist account.
  //
  // The two fields are NOT separate auth sources — both live on the
  // same `UserData` record sourced from `/api/Account/GetUserDataToken`
  // (with `role` also re-populated from JWT claims as a floor). See
  // `auth-api.ts:74,94` for the type. They are two orthogonal columns
  // of the same identity row:
  //   • `role`          — high-level bucket (orgAdmin / staff / doctor)
  //   • `tenantRoleId`  — numeric specialisation within the practice
  // A logged-in user has both populated by the time `auth-store`
  // reaches `authenticated`; the disjunction here is intentional —
  // the tab opens for users in either bucket.
  //
  // SECURITY (PR #295 §3 ESCALATE → Andrew direction 2026-05-21):
  // The backend does NOT enforce a role gate on the Lab-linkage
  // endpoints. **Andrew accepted this posture** on the rationale
  // that the upload chain (`UploadPatientDocument` /
  // `AddUpdatePatientDocument`) is intentionally cross-role —
  // shared with the regular Documents upload UX. Quote: "any role
  // should/can be able to upload." Gating only the status-transition
  // calls would create an inconsistent picture without preventing
  // the actual write. The UI gate is therefore deliberately lenient
  // (Phlebotomists + OrgAdmins) rather than restrictive — it scopes
  // the surface to roles that have a legitimate operational reason
  // to use it, but is not a security boundary.
  //
  // Defence model: UI gate (this) + feature flag (below) + in-dialog
  // PHI safeguards (file picker, server-issued doc id, review step).
  // NOT a security boundary against a determined authenticated
  // caller — see the hook doc-comments.
  //
  // Additive gate (Andrew 2026-06-09, Samer PR #529 review #5): the Order
  // Requests (Lab-linkage) tab shows only when the org-wide feature flag is
  // ON **and** the user is a Phlebotomist OR an OrgAdmin
  // (`canSeeOrderRequests`). The flag is the org-wide kill-switch (no deploy
  // needed to pull the surface); the role is the per-user gate. Both must
  // pass — neither alone is sufficient.
  const labLinkageEnabled =
    import.meta.env.VITE_LAB_LINKAGE_ENABLED === 'true';
  // Referrals tab (PROD-1395): behind the `referrals` practice switch, OFF by default.
  const referralsEnabled = useReferralsEnabled();
  // The location the Calendar is showing: the first one picked in its location filter, else the first of
  // the Calendar's own org locations (the list the toolbar and the booking panel use).
  const referralsLocationFilter = useSchedulingStore((s) => s.selectedLocationIds);
  const referralsLocation =
    organizationLocations.find((l) => l.id && referralsLocationFilter.includes(l.id)) ??
    organizationLocations.find((l) => l.id) ??
    null;
  const showLabLinkage = useAuthStore(
    (s) => labLinkageEnabled && canSeeOrderRequests(s.user),
  );

  // ── Technician Worklist / "Device Sessions" tab (PROD-2007) ────────────
  // Same two-part shape as the Lab-linkage gate above: the org-wide flag is
  // the kill-switch, `canSeeTechnicianWorklist` (Technician tenant role '21'
  // OR OrgAdmin role '1') is the per-user gate, and BOTH must pass.
  //
  // WHY THE FLAG SHIPS OFF, plainly: **the Technician role has no login
  // today.** `TECHNICIAN_TENANT_ROLE_ID = '21'` exists as a tenant role
  // (`008-technician-role.sql`), but no user can currently sign in as one —
  // it has no permission set behind it. So with the flag on, the only person
  // who can actually reach this tab is an OrgAdmin. Shipping it dark is not
  // caution about the screen; it is that the audience the screen was built
  // for cannot log in yet. That is a backend/identity gap, already raised
  // with the team, and it is what has to close before the flag goes on.
  //
  // NOT A SECURITY BOUNDARY — the same honest caveat the Order Requests gate
  // carries. The appointment endpoints this worklist reads and writes do not
  // enforce a Technician role server-side, so any authenticated caller who
  // knows the URL can fetch and mutate exactly the same data directly. What
  // this gate buys is (a) a happy-path affordance — the tab only appears for
  // people with a reason to use it — and (b) a blast-radius limit on the
  // client cache, because a surface that never mounts never pulls patient
  // rows into React Query for someone who had no business loading them.
  const technicianWorklistEnabled =
    import.meta.env.VITE_TECHNICIAN_WORKLIST_ENABLED === 'true';
  const showTechnicianWorklist = useAuthStore(
    (s) => technicianWorklistEnabled && canSeeTechnicianWorklist(s.user),
  );

  // ── Role confinement / "the worklist IS the app" (PROD-2396) ───────────
  // Andrew 2026-09-20: a Technician "should open only the page of the device
  // lists". The rail is collapsed to Calendar-only by
  // `@/app/lib/nav-confinement.ts`; this is the other half — inside the
  // calendar they get the Device Sessions tab and nothing else, and they LAND
  // on it instead of on the practitioner day grid.
  //
  // `isNavConfined` already ANDs the roster with
  // `VITE_TECHNICIAN_WORKLIST_ENABLED` — the same flag `showTechnicianWorklist`
  // reads — so there is nothing left to AND here and no dead conjunct pretending
  // otherwise. Flag off ⇒ not confined ⇒ this frame behaves exactly as it does
  // today, and the rail is full too: the confinement and the surface it confines
  // to move together (Andrew 2026-09-20). See `nav-confinement.ts` for why the
  // half-state it replaced was worse than either end.
  //
  // Confined therefore IMPLIES `showTechnicianWorklist`: confinement requires
  // the flag, and `canSeeTechnicianWorklist` admits every Technician. So the
  // landing tab below can never be a tab whose body is short-circuited out.
  //
  // NOT A SECURITY BOUNDARY, the same caveat the two gates above carry: the
  // endpoints behind every tab this removes enforce no role at all
  // (PROD-2388). This narrows what is offered, not what is reachable.
  // The tab a confined role is kept on (Technician: Device Sessions; Phlebotomist: Order Requests,
  // 23 Oct 2026), or null. `confinedToWorklist` keeps its name: it is true for every confined role.
  const confinedTab = useAuthStore((s) => confinedTabOf(s.user));
  const confinedToWorklist = confinedTab !== null;
  // Lazy initialiser, not an effect: the confined user's first paint is the
  // worklist. Switching after mount would flash the practitioner calendar and
  // fire its queries on the way past.
  //
  // WHAT HAPPENS ON A MID-SESSION ROLE CHANGE (verified, not hedged):
  //
  //  - The client learns about it at the next TOKEN REFRESH. `auth-store`'s
  //    post-refresh re-sync calls `GetUserDataToken` and merges the response
  //    into `user` (auth-store.ts, "PROD-894 Phase 7"), so `tenantRoleId`
  //    updates without a logout. Refresh is lazy, not scheduled: it fires
  //    from `getValidAccessToken()` on the next authenticated API call once
  //    the access token is within 30s of expiry (`jwt.ts isExpiringSoon`).
  //    So the upper bound is one access-token lifetime plus one API call —
  //    seconds-to-minutes in practice, NOT "needs a logout".
  //  - Everything that reads `user` through a store selector then updates on
  //    that render: the nav rail, the route guard, the account menu, the
  //    toolbar gates, and `confinedToWorklist` itself.
  //  - `activeTab` does NOT, because this initialiser runs once per mount.
  //    FrameScheduling unmounts only when `activeNav` leaves 'calendar'
  //    (App.tsx's render switch) or the page reloads.
  //
  // So, concretely: a user newly GRANTED Technician gets the collapsed rail
  // within one refresh but stays on whatever tab they were on until they
  // reload — and they cannot navigate away to force a remount, because the
  // rail now has one button. A user whose Technician role is REVOKED gets
  // the full rail back within one refresh, and the first click on any other
  // nav item unmounts this frame, so returning to Calendar lands on the
  // ordinary default. Only the grant direction needs the reload, and a role
  // grant is an admin action already followed by telling the person to
  // refresh.
  // Back from the lab desk's review (Andrew, 23 Oct; behind `intake-desk`): `?tab=order-requests` opens Order Requests.
  const { pathname: framePath, search: frameSearch } = useLocation();
  const [activeTab, setActiveTab] = useState<SchedulingTab>(() =>
    initialSchedulingTab(confinedTab, frameSearch, isIntakeDeskOn()),
  );
  // Flutter hides the toolbar settings gear for Phlebotomists
  // (`calendar_screen.dart:2808` — `isPhlebotomistRole`).
  const isPhlebotomist = useAuthStore((s) => isPhlebotomistRole(s.user));
  // PROD-968: opening a block tile reveals the BlockAvailabilityPanel's
  // edit/unblock actions, so gate the open on `Doctor_Calender` edit — the
  // permission Flutter uses for block/appointment edit (calendar_tab.dart:
  // 2243-2245). Block tiles stay visible on the calendar (read-only); only
  // the edit panel is gated. Applied uniformly to every view's block click.
  const canEditBlock = usePermission(['Doctor_Calender'], { edit: true });
  const [cancelTarget, setCancelTarget] = useState<DayListRow | null>(null);
  // Reschedule fallback target. The primary reschedule path opens the inline
  // edit panel (Flutter parity — reschedule is a left-panel edit, never a
  // dialog); this dialog is only a safety net for the near-impossible case of a
  // row missing the ids the panel needs to PreLoad.
  const [rescheduleTarget, setRescheduleTarget] = useState<DayListRow | null>(null);
  const [noteTarget, setNoteTarget] = useState<DayListRow | null>(null);
  // PVS (Post-Visit Summary) target — the row whose "View PVS" overflow item
  // was clicked (PROD-1651). Opens the shared PostVisitSummaryDialog.
  const [pvsTarget, setPvsTarget] = useState<DayListRow | null>(null);
  const navigate = useNavigate();

  /**
   * Build the canonical view-appointment URL for a row. The panel reads
   * `appointmentId` from the path segment and `patientId / providerId /
   * profileId / tab` from query params (`view-appointment-panel-page.tsx`
   * §`hasFullContext`). When the row is missing any of the three context
   * IDs the panel falls back to the "open from calendar" empty state —
   * the navigation still works, the panel just surfaces the empty state
   * instead of fetching.
   *
   * `tab` is one of the panel's TabKey values (`patient` | `vitals` |
   * `forms` | `docs` | `billing` | `appointments`). Omitted on the default
   * Patient tab to keep URLs tidy.
   */
  const buildViewAppointmentUrl = (row: DayListRow, tab?: string): string => {
    const params = new URLSearchParams();
    if (row.patientId) params.set('patientId', row.patientId);
    if (row.practitionerId) params.set('providerId', row.practitionerId);
    if (row.appointmentProfileId) params.set('profileId', row.appointmentProfileId);
    if (tab && tab !== 'patient') params.set('tab', tab);
    // Return context — lets the panel's in-app Back button land on the exact
    // calendar day/view the user came from instead of the default today.
    const y = selectedDate.getFullYear();
    const mo = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const da = String(selectedDate.getDate()).padStart(2, '0');
    params.set(
      'from',
      `/?date=${y}-${mo}-${da}&view=${calendarView}${devicesActive ? '&actor=devices' : ''}`,
    );
    const qs = params.toString();
    return `/scheduling/appointment/${row.id}${qs ? `?${qs}` : ''}`;
  };

  const handleRowAction = (row: DayListRow, action: AppointmentActionId) => {
    switch (action) {
      case 'cancel':
        setCancelTarget(row);
        return;
      case 'reschedule':
        // Reschedule — recurring OR not — opens the inline left panel in
        // reschedule mode (Flutter parity: `calendar_tab.dart:2242-2253` fires
        // ChangeLeftPanelTypeEvent(true) → left panel, with NO scope picker and
        // NO date/time dialog. PM saves a reschedule as PUT /Appointment/{id}
        // affecting only that occurrence — `editRecurring` is never set on the
        // calendar path, so a recurring occurrence reschedules in isolation).
        // The inline panel needs all four ids for the PreLoad fetch; only if a
        // row is missing one do we fall back to the stopgap date/time dialog.
        if (row.patientId && row.practitionerId && row.appointmentProfileId) {
          openEditPanel({
            appointmentId: row.id,
            patientId: row.patientId,
            providerId: row.practitionerId,
            profileId: row.appointmentProfileId,
            mode: 'reschedule',
          });
          return;
        }
        setRescheduleTarget(row);
        return;
      case 'add-note':
        setNoteTarget(row);
        return;
      case 'view-pvs':
        // PROD-1651 — Flutter `calendar_tab.dart:2638-2639` calls openPvs(id).
        // React opens the shared Post-Visit Summary dialog for the row's appt.
        setPvsTarget(row);
        return;
      case 'open-billing-tab':
        // PROD-915 — Flutter `calendar_tab.dart:2657-2658` opens the edit
        // screen with `isBilling: true`. The React equivalent is the view
        // panel's `?tab=billing` query param. The menu item already
        // gates on Show_Billing_Tab + status, so by the time we get here
        // the user is authorised.
        navigate(buildViewAppointmentUrl(row, 'billing'));
        return;
      case 'view-recurring-series':
        // PROD-916 — Flutter `calendar_tab.dart:2671-2677` opens the edit
        // screen with `isViewRecurringSeries: true`. The panel renders the
        // recurring series via the Appointments tab, which is the
        // closest React equivalent (visible only on recurring rows in
        // `view-appointment-panel-page.tsx:128`).
        navigate(buildViewAppointmentUrl(row, 'appointments'));
        return;
      case 'edit':
        // PROD-918 — Flutter `calendar_tab.dart:2585-2594` (overflow Edit
        // Appointment item) AND `appointment_status.dart` Pending-row Edit
        // pencil both call `widget.onSelect!(appt, context, false, false)`
        // which navigates to the edit screen. The React equivalent is the
        // canonical view-appointment URL with the default Patient tab —
        // same path the popup pencil uses (`scheduling-page.tsx:643`).
        //
        // Flutter wraps the Edit dispatch in a permission check
        // (`Manage_Patient_Appointments view` OR `userData.id == provider.id`)
        // and falls back to a ShowAlertEvent on denial. React's view-
        // appointment panel hides destructive actions behind its own
        // permission gates per-tab — the calendar-side check is therefore
        // not a security boundary, and we let the user navigate; if they
        // lack permission to actually mutate they'll see the read-only
        // view. Matches the pencil-from-popup flow which has no
        // pre-navigation gate either.
        navigate(buildViewAppointmentUrl(row));
        return;
      case 'follow-up':
        // PROD-919 — Flutter `calendar_tab.dart:2306-2307` fires
        // `widget.onSelect!(appointment, context, false, true)` on
        // `bookFollowUpAppointment` — the trailing `true` is `isFollowUp`
        // and tells the parent to open the create panel pre-filled from
        // the source appointment. Action visibility (Fulfilled +
        // !isRecurring) is already enforced upstream by
        // `getRowActionsFor('fulfilled')` (`appointment-actions.ts:120-129`).
        //
        // The pre-fill shape mirrors `handleFuCreateFollowUp` above
        // (PROD-920 — FU Requests tab create flow) but seeds from a
        // DayListRow instead of a FuRequestResponse:
        //   - patient context (id + name + photo) — DayListRow only
        //     carries display names not the structured fields the FU
        //     path uses (dob/gender absent), so we seed the minimum
        //     needed for the participant picker and let the user
        //     re-fetch on demand if a deeper field is needed.
        //   - provider context (id + name + speciality).
        //   - location seeded from the user's first org location
        //     (Flutter `calendar_screen.dart:180`).
        //   - reasonCode carried forward as the seed value (Flutter
        //     `calendar_screen.dart:170`).
        //   - referenceAppointmentId = source `row.id` — this is the
        //     signal that locks the profile picker to FOLLOWUP-type
        //     profiles only (`appointment-profile-section.tsx`).
        //   - selectedDate defaults to the calendar's selected day so
        //     the date strip lands on the right day; the user can pick
        //     a future day for the follow-up itself.
        {
          const userLocations =
            useAuthStore.getState().user?.organizationLocations ?? [];
          const firstLocation = userLocations[0]?.id ? userLocations[0] : null;
          // Clamp the seed date so a follow-up never opens on a past day
          // (PR #425 review §5). Shared helper keeps this in lockstep with
          // the popup follow-up in scheduling-page — the two paths are
          // flagged for parallel drift.
          const followUpInitial = buildFollowUpInitialFromRow(
            row,
            firstLocation,
            followUpSeedDateISO(selectedDate, new Date()),
          );
          if (MODAL_BOOKING) {
            // Experiment (Antonin, 14 Oct): row-action follow-up opens the
            // central modal instead of the inline side panel.
            setModalBooking({ initialValues: followUpInitial });
          } else {
            startManualFlow(); // manual FU action — clears + suspends (audit #2)
            setPendingInitialValues(followUpInitial);
            setActivePanel('appointment');
          }
        }
        return;
      // Status-update actions don't reach this dispatcher — the row
      // handler in `day-list-calendar.tsx` intercepts them and fires
      // `useUpdateAppointmentStatus` directly (see `statusCodeForAction`
      // routing at L463-471 above). Listed here for exhaustiveness so
      // adding a new `AppointmentActionId` value triggers a TS error
      // at this site, not a silent runtime no-op. (Samer review on
      // PR #343 §1 — "if something upstream sends an unexpected
      // action type post-refactor, it'll fail invisibly.")
      case 'mark-arrived':
      case 'mark-no-show':
      case 'mark-checked-in':
      case 'confirm':
      case 'unmark-arrived':
      case 'unmark-checked-in':
      case 'unmark-no-show':
      case 'overflow':
        return;
      default: {
        // Exhaustive type-check — if a new `AppointmentActionId` value
        // is added without a corresponding case here, TS flags this
        // assignment as `Argument of type '"new-action"' is not
        // assignable to parameter of type 'never'`. Runtime warns so
        // a missed case in production logs a clear signal instead of
        // silently swallowing the click.
        const _exhaustive: never = action;
        // eslint-disable-next-line no-console
        console.warn(
          `[frame-scheduling] handleRowAction received unhandled action: ${_exhaustive}`,
        );
        return;
      }
    }
  };
  // Calendar view — controlled by parent when `controlledCalendarView`
  // is supplied (Samer review #81 §1 — lets SchedulingPage gate the
  // month-range fetch on activeView=='Month'). Falls back to internal
  // state otherwise.
  const [internalCalendarView, setInternalCalendarView] = useState<DateFilter>('Day');
  const calendarView = controlledCalendarView ?? internalCalendarView;
  const setCalendarView = (v: DateFilter) => {
    if (onCalendarViewChange) onCalendarViewChange(v);
    else setInternalCalendarView(v);
  };

  // Dev-time guard (review §4): By-Doctor tiles degrade gracefully to
  // click-only when `onByDoctorAppointmentDrop` is absent (the
  // `draggable={!!onAppointmentDrop}` gate means no drag listeners attach —
  // it's not a silent no-op, drag simply isn't offered). Still, warn in dev
  // so a missing wire-up in some future render path is visible rather than a
  // quietly disabled feature.
  useEffect(() => {
    if (
      import.meta.env.DEV &&
      calendarView === 'Doctor' &&
      !onByDoctorAppointmentDrop
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[frame-scheduling] By-Doctor view active without `onByDoctorAppointmentDrop` — drag-to-reschedule is disabled (tiles are click-only).',
      );
    }
  }, [calendarView, onByDoctorAppointmentDrop]);
  const [listView] = useState(false);
  // Provider strip expand/collapse — chevron lives in CalendarSubToolbar
  // (Flutter renders it on the sub-toolbar line, not inside the strip).
  // `canExpand` reflects whether providers actually overflow one row at
  // the current width; the strip emits it via `onCanExpandChange`.
  const [providerListExpanded, _setProviderListExpanded] = useState(false);
  const [_providerListCanExpand, setProviderListCanExpand] = useState(false);
  // The practitioner filters (specialties + the practitioners) live behind the Practitioners button when the page has
  // the Practitioners / Devices bar (Antonin, 13 Oct); without that bar they keep their rows.
  const [practitionerPanelOpen, setPractitionerPanelOpen] = useState(false);
  const closePractitionerPanel = useCallback(() => setPractitionerPanelOpen(false), []);

  // Specialty + provider selection live in the persisted Zustand store so
  // they survive reloads (matches Flutter's selected_specialities /
  // selected_providers prefs, scoped per user id).
  const selectedSpecialties = useSchedulingStore((s) => s.selectedSpecialties);
  const toggleSpecialty = useSchedulingStore((s) => s.toggleSpecialty);
  const clearSpecialties = useSchedulingStore((s) => s.clearSpecialties);
  const selectedProviderIds = useSchedulingStore((s) => s.selectedProviderIds);
  const toggleProvider = useSchedulingStore((s) => s.toggleProvider);
  const setSelectedProviders = useSchedulingStore((s) => s.setSelectedProviders);
  const clearProviders = useSchedulingStore((s) => s.clearProviders);
  // Inline edit/reschedule rail target (set by the appointment popup pencil /
  // Reschedule). When set, it owns the left rail and the create/block panels
  // stay closed (single-rail invariant, mirrors Flutter's one left-panel slot).
  const editTarget = useSchedulingStore((s) => s.editTarget);

  // Audit #3, third unclean route: entering inline-edit (editTarget from
  // the scheduling store) unmounts the create rail via `open={... &&
  // !editTarget}` WITHOUT firing onClose — the AI layer must die with the
  // rail here too, or ghosts keep painting with no rail on screen. Uses
  // the previous-render adjustment pattern, not an effect (repo rule
  // react-hooks/set-state-in-effect — Codex P1 on c881f983). Also closes
  // the HIDDEN create rail + its prefill (Codex P2): with `activePanel`
  // left as 'appointment', closing the edit would silently remount the
  // create rail with the stale AI provider/profile/date but no AI context.
  const [prevEditTarget, setPrevEditTarget] = useState(editTarget);
  if (editTarget !== prevEditTarget) {
    setPrevEditTarget(editTarget);
    if (editTarget) {
      // Inline edit is a manual flow: suspend in-flight parses (audit #2)…
      setAiSuspendSeq((s) => s + 1);
      if (aiDraft) {
        // …and tear down the AI layer + the hidden prefilled rail (#3, P2).
        setAiDraft(null);
        setAiRejected(false);
        setAiFillReport(null);
        setActivePanel(null);
        setPendingInitialValues(null);
      }
    }
  }
  const openEditPanel = useSchedulingStore((s) => s.openEditPanel);

  /* Per-org booking-rail geometry gate (VITE_BOOKING_RAIL_WIDE_ORGS):
     'classic' (left + 380px, pre-r2 prod parity) unless this org is on the
     allow-list ('*' in dev). Single resolution point — all three rails and
     the DOM placement below read the SAME value, so side/width/order can
     never disagree between create, inline-edit and block. */
  const railLayout = useBookingRailLayout(organizationId);
  const railEdge: 'left' | 'right' = railLayout === 'wide' ? 'right' : 'left';

  const bookingRails = (
    <>
      {/* Booking rails — geometry per the per-org gate (use-booking-rail-layout):
          wide = RIGHT + ~one-third (Samer 2026-08-12: "two-thirds for the
          scheduling, one-third for the rail" — drawn for the Scheduling-AI
          rail); classic = LEFT + 380px (pre-r2 production parity). The frame
          places this fragment BEFORE or AFTER the calendar in the DOM per
          mode, so reading/focus order always matches the visual order (no
          flex-row-reverse in either mode). Only one of Create / InlineEdit /
          Block / BookDevice renders at a time (single-rail invariant, Flutter
          parity) — the device rail joined them in PROD-2186. */}
      <CreateAppointmentPanel
        edge={railEdge}
        layout={railLayout}
        open={showCreate && !editTarget}
        onClose={() => {
          // A chain advance just re-opened the rail for the next leg — the
          // post-save close must not tear it down (see advanceAiChain).
          if (aiChainAdvancedRef.current) {
            aiChainAdvancedRef.current = false;
            return;
          }
          setActivePanel(null);
          setPendingInitialValues(null);
          // Ghost drafts live exactly as long as the rail: close/discard
          // clears them (Scheduling-AI lifecycle rule). No-op when no
          // draft is active.
          clearAiDraft();
        }}
        organizationId={organizationId}
        practiceId={practiceId}
        userId={userId}
        onSearchPatients={onSearchPatients}
        initialValues={pendingInitialValues ?? undefined}
        // Patient-search seed from the active AI draft (undefined when
        // the flag is off or the rail opened via any non-AI path).
        initialPatientQuery={
          // A human-confirmed patientId is already prefilled — seeding the
          // SEARCH too would reopen the picker for a settled question.
          schedulingAiEnabled && !aiDraft?.patientId
            ? aiDraft?.patientQuery
            : undefined
        }
        aiFillReport={schedulingAiEnabled ? (aiFillReport ?? undefined) : undefined}
        // Completion rail (Option B): the panel renders the generated
        // checklist from the draft; the chip strip above is its no-draft
        // fallback. Dismiss (the rail's ×) drops the draft + ghosts and
        // leaves the manual rail exactly as it stands.
        aiDraft={schedulingAiEnabled ? aiDraft : undefined}
        // PROD-1846 verdict surfaces IN the rail as the ConflictCard (whose
        // tap opens time/date selection) — the amber ghosts alone gave the
        // user nothing tappable (Codex P2 on the rail commit).
        aiConflict={
          // Book-time rejection (audit #8) outranks the parse-time verdict —
          // it's fresher and about the exact slot the user just tried.
          schedulingAiEnabled && aiBookConflict
            ? 'That time was just taken — pick another slot.'
            : schedulingAiEnabled && aiRejected
              ? 'That day looks unavailable for the requested time — pick another slot.'
              : undefined
        }
        onAiSlotConflict={
          schedulingAiEnabled ? () => setAiBookConflict(true) : undefined
        }
        onAiConflictResolved={
          schedulingAiEnabled
            ? () => {
                // Replacement slot committed — both verdicts clear (Codex P1).
                setAiBookConflict(false);
                setAiRejected(false);
              }
            : undefined
        }
        onAiDismiss={schedulingAiEnabled ? clearAiDraft : undefined}
        // Recurring drafts land straight on the recurrence configurator — but
        // ONLY when provider + profile resolved from the prompt (Codex review
        // on #836: without them the configurator's availability check silently
        // no-ops and the landing is a dead end). Unresolved → the checklist
        // guides from the create view instead.
        aiInitialView={
          // slotStart is REQUIRED for this landing (audit #1): without it
          // the recurrence picker skips its end-date fetch, "Check
          // availability" silently no-ops and Save stays disabled forever —
          // the only exit is Close. AI drafts never set a slot by design
          // (ai-draft-prefill), so today this condition is never true for
          // them and the checklist guides from the create view instead; the
          // rail's own recurrence-dates CTA routes here AFTER a slot exists.
          schedulingAiEnabled &&
          aiDraft?.recurrence &&
          pendingInitialValues?.providerId &&
          pendingInitialValues?.appointmentProfileId &&
          pendingInitialValues?.slotStart
            ? 'recurrence-config'
            : undefined
        }
        aiDraftKey={
          schedulingAiEnabled && aiDraft
            ? [
                aiDraft.start ?? '',
                aiDraft.recurrence
                  ? `${aiDraft.recurrence.period}x${aiDraft.recurrence.count}`
                  : '',
                aiDraft.chain?.length ?? 0,
                aiDraft.doctorQuery ?? '',
                aiDraft.profileQuery ?? '',
              ].join('~')
            : undefined
        }
        onSaveSuccess={schedulingAiEnabled ? advanceAiChain : undefined}
      />
      {/* Inline edit/reschedule rail (popup pencil / Reschedule → edit-in-place,
          Flutter parity). Same edge/layout as the create rail; self-hides
          until a target is set. Mutually exclusive with create/block above. */}
      <InlineEditAppointmentPanel
        edge={railEdge}
        layout={railLayout}
        organizationId={organizationId}
        practiceId={practiceId}
        userId={userId}
        onSearchPatients={onSearchPatients}
        calendarDateYmd={ymdLocal(selectedDate)}
        // PROD-2526 — a device booking opens the Book-a-device rail in edit mode.
        devices={devicesTab?.devices ?? []}
        locations={organizationLocations}
        onEditSaveSuccess={(newStart) => {
          // Follow a reschedule that moved the appointment to another DAY.
          // Same-day time changes leave the calendar alone.
          if (!newStart) return;
          const next = new Date(newStart);
          if (Number.isNaN(next.getTime())) return;
          if (ymdLocal(next) === ymdLocal(selectedDate)) return;
          onSelectedDateChange(next);
        }}
      />
      {/* PROD-2515 — REMOUNTED PER OPEN, exactly as BookDevicePanel below is.
          The panel's actor kind and start date are constructor arguments with
          no setters, by design, so it has to be constructed when the entry
          point says it is: this rail lives here and would otherwise stay
          mounted for the session, initialised once as a practitioner panel on
          today's date, ignoring both props ever after. See `blockOpenSeq`. */}
      <BlockAvailabilityPanel
        key={`${blockActorKind}:${blockOpenSeq}`}
        edge={railEdge}
        layout={railLayout}
        open={showBlock && !editTarget}
        onClose={() => {
          setActivePanel(null);
          setEditingBlockId(null);
        }}
        organizationId={organizationId}
        practiceId={practiceId}
        blockId={editingBlockId}
        /* PROD-2454 — which actor the panel is blocking, and (for a device)
           the list its picker offers. `blockableDevices` is the page's
           narrowing, NOT `devicesTab.devices`: that list is the BOOKABLE one
           (working hours AND a product) and blocking needs only the hours,
           so reusing it would hide a machine that can perfectly well be
           taken out of service. See `isBlockableDevice`.

           Both are passed unconditionally. Guarding them on
           `blockActorKind === 'device'` would only move the decision one
           level up from the panel, which already ignores them on a
           practitioner block. */
        actorKind={blockActorKind}
        /* PROD-2515 — open on the day the calendar is showing, as the
           Book-a-device rail always has. Read once at construction, which the
           key above now guarantees actually happens per open; after that the
           date is the operator's to change inside the form. */
        initialDate={selectedDate}
        devices={devicesTab?.blockableDevices ?? []}
        devicesLoading={devicesTab?.isLoading ?? false}
      />
      {/* Book a device (PROD-2007, Scheduling #1964) — a RAIL since PROD-2186,
          not a dialog. It sits in THIS fragment so it inherits the same
          before/after-calendar placement per mode and the same edge/width as
          the other three; `showDevice` is the same single enum, so it can
          never share the screen with them, and `!editTarget` lets inline-edit
          outrank it exactly as it outranks create and block. Still remounted
          per open via `seq` so the header / empty-cell preselection is read
          fresh. Mounted only on the Devices tab. */}
      {devicesTab ? (
        <BookDevicePanel
          key={bookDevice?.seq ?? 'none'}
          edge={railEdge}
          layout={railLayout}
          open={showDevice && !!bookDevice && !editTarget}
          onOpenChange={(next) => {
            if (!next) {
              setActivePanel(null);
              setBookDevice(null);
            }
          }}
          organizationId={organizationId}
          practiceId={practiceId}
          userId={userId}
          devices={devicesTab.devices}
          initialDeviceId={bookDevice?.deviceId ?? null}
          initialStartISO={bookDevice?.startISO ?? null}
          selectedDate={selectedDate}
          locations={organizationLocations}
        />
      ) : null}
    </>
  );

  // The calendar's natural-language booking bar ("Tell Scrips what to schedule…" + demo chips) between the header and
  // the practitioners: off since 15 Oct (Antonin). Flip to bring it back.
  const CALENDAR_AI_BAR = false;
  const aiCommandBar = (
            schedulingAiEnabled && organizationId && !confinedToWorklist ? (
              <SchedulingAiCommandBar
                resolvePractitionerId={(doctorQuery) => {
                  const words = doctorQuery?.toLowerCase().split(/\s+/) ?? [];
                  if (words.length === 0) return undefined;
                  return providers.find((p) => {
                    const n = p.name.toLowerCase();
                    return words.every((w) => n.includes(w));
                  })?.id;
                }}
                organizationId={organizationId}
                practiceId={practiceId}
                // LIVE intent lane (PROD-1316): the roster the strip already
                // holds, as candidates for the model. Empty roster → the bar
                // stays on the offline grammar.
                intentCandidates={providers.map((p) => ({
                  id: p.id,
                  name: p.name,
                  speciality: p.specialty,
                  profiles: (p.quickBookGroups ?? [])
                    .flatMap((g) => g.profiles)
                    .filter((pr) => !!pr.id && !!pr.profileName)
                    .map((pr) => ({
                      id: pr.id as string,
                      name: pr.profileName as string,
                      durationMinutes: pr.minutesDuration ?? null,
                    })),
                }))}
                onDraft={handleAiDraft}
                onRejectedChange={setAiRejected}
                // Example-prompt chips are practitioner utterances end to end,
                // so they leave the Devices tab (Andrew, 2026-09-13). The
                // field, the Ask button and the PHI warning stay on both tabs.
                showExamplePrompts={!devicesActive}
                // Manual-flow suspend counter — the bar drops parses that
                // were in flight when a manual booking started (audit #2).
                suspendSeq={aiSuspendSeq}
                // The bar's × also dismisses the placed rail draft AND the
                // rail it opened — the undo lives where the mistake was
                // typed (Samer, 2026-08-12; Codex P1).
                onClearDraft={undoAiRequest}
              />
            ) : undefined
  );


  const appointmentSearch = !isPhlebotomist && !confinedToWorklist && organizationId ? (
    <AppointmentSearch
      organizationId={organizationId}
      practiceId={practiceId}
      selectedDate={selectedDate}
      providerIds={providers.map((p) => p.id)}
    />
  ) : undefined;

  const dateControlSlot = (
    <span className="flex items-center gap-2">
      <CalendarDateControl value={selectedDate} onChange={onSelectedDateChange} label={periodLabel(selectedDate, calendarView)}
        triggerClassName="h-8 px-2 tabular-nums text-[length:var(--type-body-size)]" />
      <button type="button" aria-label="Previous" onClick={() => onSelectedDateChange(stepPeriod(selectedDate, calendarView, -1))}
        className="grid h-8 w-8 place-items-center rounded-md text-text-secondary hover:bg-grey-100"><ChevronLeft className="h-4 w-4" /></button>
      <button type="button" onClick={() => onSelectedDateChange(new Date())} disabled={showsToday(selectedDate, calendarView)}
        className="h-8 rounded-md border border-border-default px-3 text-[length:var(--type-body-size)] font-medium text-text-primary hover:bg-grey-100 disabled:opacity-50">Today</button>
      <button type="button" aria-label="Next" onClick={() => onSelectedDateChange(stepPeriod(selectedDate, calendarView, 1))}
        className="grid h-8 w-8 place-items-center rounded-md text-text-secondary hover:bg-grey-100"><ChevronRight className="h-4 w-4" /></button>
    </span>
  );

  return (
    <div className="h-full flex bg-[var(--color-surface-card)] overflow-hidden">

      {railLayout === 'classic' && bookingRails}

      <div className="flex-1 flex flex-col min-w-0 overflow-clip">
        {/* 1. Top toolbar (tabs/search/practice/create) + specialty chips row */}
        <CalendarToolbar
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            dropOrderRequestsTab(tab, framePath, frameSearch, navigate);
          }}
          practiceName={practiceName}
          // The practice lives in the top bar since 15 Oct (PRACTICE_IN_BAR): the Calendar header carries no selector.
          practiceSwitcher={PRACTICE_IN_BAR ? <></> : organizationId ? (
              // Next to "Calendar" (Antonin, 13 Oct), 28 px tall so the title stays on the header's 16 px top line. In a
              // narrow window it gives way first (shrinks 100x faster, its name truncating) so "Calendar" is never cut.
              <PracticeSwitcher organizationId={organizationId} className="h-8 data-[size=default]:h-8 py-0 min-w-0 shrink-[100]"
                // The practice settings live in this menu, one per row (Antonin, 13 Oct); confined roles get none
                // (PROD-2396: the settings page is a second screen the route guard would bounce).
                onOpenSettings={!isPhlebotomist && !confinedToWorklist
                  ? (id) => navigate(`/settings/practices/${id}`, { state: { from: '/' } })
                  : undefined} />
            ) : undefined
          }
          // Confined roles (PROD-2396) get no search at all — picking a
          // result calls `navigate('/scheduling/appointment/:id')`
          // (`appointment-search.tsx:113`), a second screen the route guard
          // would bounce, and the dropdown lists patient names to someone
          // confined to a device worklist.
          //
          // That suppression is NOT done here, deliberately. Passing
          // `undefined` falls THROUGH to the toolbar's own placeholder input
          // (`calendar-toolbar.tsx`, the `??`), so gating the slot here would
          // swap a live search for a dead one rather than remove it. The
          // toolbar owns its chrome and suppresses the whole search on
          // `confinedToTabId` — one gate, one test. A second copy here was
          // written, proved unreachable by mutation testing, and removed.
          // Scheduling-AI command bar — STRICTLY flag-gated (fail-closed):
          // when VITE_SCHEDULING_AI is absent/false the slot is undefined
          // and the toolbar renders no AI row at all. Also gated on
          // organizationId like searchSlot above (audit S3): without it the
          // alternatives POST fires with organizationId:'' — malformed,
          // even though tenant headers keep it same-tenant.
          //
          // Confined roles (PROD-2396) are excluded here rather than in the
          // toolbar, unlike the search: the toolbar renders this slot only
          // when it is provided, with no placeholder fallback, so the gate
          // has to be on the supply side to have any effect. Booking by
          // natural language is not the job of someone confined to running
          // a machine's worklist.
          // The assistant lives in the floating Scrips Intelligence (IntelligenceFab, the default since 12 Oct), not the toolbar.
          // Hidden (Antonin, 15 Oct: "remove the llm part between header and practitioners"): the row is off by
          // default; CALENDAR_AI_BAR brings it back. The ghost-draft and completion-rail code paths stay intact.
          aiCommandSlot={CALENDAR_AI_BAR ? aiCommandBar : undefined}
          // Practitioners / Devices (PROD-2007) — only when the page supplies
          // the Devices tab; undefined keeps the toolbar exactly as it was.
          // Practitioners / Devices (PROD-2007): its own bar above the filters (Antonin, 13 Oct), only when the page
          // supplies the Devices tab.
          // The view switch sits at the far right of the Practitioners / Devices row (Antonin, 13 Oct: "same line as
          // practitioners / device, opposite side of the row"); without that row it stays in the sub-toolbar.
          // The same search on the tabs without a date row (FU / Order requests): the toolbar puts it in the header.
          searchSlot={appointmentSearch}
          actorTrailing={
            <div className="flex items-center gap-3">
              {appointmentSearch}
              <ViewToggleGroup value={calendarView} onChange={setCalendarView} views={devicesActive ? DEVICE_CALENDAR_VIEWS : undefined} />
            </div>
          }
          actorTabs={dateControlSlot}
          actorCenter={devicesTab ? (
            <CalendarActorTabs
              value={devicesTab.actor}
              onChange={(a) => { setPractitionerPanelOpen(false); devicesTab.onActorChange(a); }}
              deviceCount={devicesTab.deviceCount}
              practitionersMenu={{
                open: practitionerPanelOpen,
                onToggle: () => setPractitionerPanelOpen((v) => !v),
                filtered: selectedSpecialties.length > 0 || (providers.length > 0 && providers.some((p) => !selectedProviderIds.includes(p.id))),
                summary: (() => {
                  const on = providers.filter((p) => selectedProviderIds.includes(p.id)).length;
                  return providers.length > 0 && on < providers.length ? `${on} of ${providers.length}` : undefined;
                })(),
              }}
            />
          ) : undefined}
          // Subtype filter replaces the specialty chips on the Devices tab —
          // HIDDEN since 2026-09-13 behind `DEVICE_SUBTYPE_FILTER_ENABLED`
          // (Andrew: "remove for now or hide only the filter of the categories
          // of the devices and keep them under all"). The row, its capping and
          // the page's selection state are all intact; the constant is the one
          // switch that brings it back. With it off the page reports an empty
          // selection, so the tab behaves as though "All" is picked.
          filterRow={
            DEVICE_SUBTYPE_FILTER_ENABLED && devicesActive && devicesTab ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="min-w-0">
                  <SpecialtyFilter
                    specialties={['All', ...visibleSubtypes.visible]}
                    selected={devicesTab.selectedSubtypes}
                    onToggle={devicesTab.onSubtypeToggle}
                    onClear={devicesTab.onSubtypeClear}
                    ariaLabel="Filter by device subtype"
                  />
                </div>
                {/* `subtype` is free text, so the row is otherwise unbounded
                    (review #4). Capped at the most-used ones; the rest are one
                    click away and a selected chip is never hidden. */}
                {visibleSubtypes.hiddenCount > 0 || showAllSubtypes ? (
                  <button
                    type="button"
                    onClick={() => setShowAllSubtypes((v) => !v)}
                    className="flex-shrink-0 rounded-full border border-border-default px-3 py-1 text-xs text-text-muted hover:bg-grey-100 hover:text-text-primary"
                  >
                    {showAllSubtypes ? 'Show fewer' : `+${visibleSubtypes.hiddenCount} more`}
                  </button>
                ) : null}
              </div>
            ) : undefined
          }
          // ...and with that row hidden the toolbar fell THROUGH to the
          // practitioner specialty chips underneath it. They go too (Andrew,
          // 2026-09-14): a device has no specialty, so the row was narrowing
          // machines by a practitioner attribute, and the ≡ menu next to it
          // drives provider checkboxes a device card never draws. Hiding it is
          // only half the fix — the page also reads the persisted selection as
          // "All" while this tab is up, so a chip left in localStorage cannot
          // keep narrowing the AI bar's practitioner roster from behind a
          // control that is no longer on screen.
          showSpecialtyFilterRow={!devicesActive && !integratedFilters && !holdActorRow}
          // Book Device is withheld from a confined role (PROD-2398), and
          // on its own merits rather than as a URL patch: booking a device
          // session is a front-desk action, not the job of the person
          // running the machine. The `?actor=devices` deep link is closed
          // separately in `confinedRedirectPath`, but this gate is the one
          // that holds even if that ever regresses.
          //
          // NOT gated on "they have no permissions anyway". They do not,
          // client-side — and PROD-2388 means the server does not check
          // either, so a permission that is only asserted in the browser is
          // not a control. This removes the control; it does not protect the
          // endpoint behind it.
          onBookDeviceClick={
            devicesActive && !confinedToWorklist
              ? () => openBookDevice(null, null)
              : undefined
          }
          showOrderRequestsTab={showLabLinkage}
          showReferralsTab={referralsEnabled}
          showTechnicianWorklistTab={showTechnicianWorklist}
          confinedToTabId={confinedTab ?? undefined}
          // Confined roles (PROD-2396) lose the gear too: it navigates to
          // `/settings/practices/:id`, which is a second screen, and the
          // route guard would bounce it straight back.
          // The gear moved into the practice menu (Antonin, 13 Oct: "this is not the place for the practice settings").
          showSettings={false}
          onSettingsClick={() => {
            // Flutter parity (`calendar_screen.dart:2806-2833`): the gear
            // opens the active practice's settings page (PracticeBaseWidget
            // for selectedPractice). React route: PracticeEditPage edit mode.
            // `from: '/'` so the page's Back / save-return lands back on the
            // calendar (Flutter's Navigator.pop), not the practices list.
            if (practiceId)
              navigate(`/settings/practices/${practiceId}`, {
                state: { from: '/' },
              });
          }}
          onCreateClick={() => {
            // Single setter, single enum — eliminates the double-click
            // race condition (Samer review §3). Toolbar Create button
            // opens FRESH — no pre-fill (PROD-829: only quick-create
            // paths from the provider strip pre-fill).
            setPendingInitialValues(null);
            // Ghost drafts live exactly as long as the rail (audit #3):
            // this route replaces/toggles the rail without firing the
            // panel's onClose, so it must clear the AI layer itself or
            // ghosts keep painting and a reopened Create resurrects the
            // stale checklist over a blank form. Also suspends in-flight
            // parses (audit #2).
            startManualFlow();
            setActivePanel((cur) => (cur === 'appointment' ? null : 'appointment'));
          }}
          onCreateBlockClick={() => {
            // Toggling Create→Block from the toolbar opens in CREATE mode
            // (no blockId). Edit mode only enters via clicking an existing
            // block tile on the calendar (handleAppointmentClick).
            openBlockPanel(BLOCK_ACTOR_PRACTITIONER, { toggle: true });
          }}
          /**
           * PROD-2454 — the Devices tab's Block entry point. Same toggle
           * semantics and same lifecycle calls as `onCreateBlockClick`
           * above; the only difference is the actor kind it opens for.
           *
           * Gated on EXACTLY the two inputs the practitioner Block item is
           * gated on, with the tab flipped: `canBlockAvailability` below is
           * `!devicesActive && !confinedToWorklist`, so this is
           * `devicesActive && !confinedToWorklist`. Same confined-role
           * exclusion (PROD-2396 — taking a machine out of service is a
           * front-desk action, not the job of the person running it), so no
           * role can block on one tab and not the other.
           *
           * There is no third input to add here yet: the real
           * `Doctor_Calender` check is still the TODO below, and it covers
           * BOTH items. Inventing a stricter gate for the device item alone
           * would be exactly the drift this mirrors away from. Chrome, not a
           * boundary — the endpoint enforces nothing (PROD-2388).
           */
          onBlockDeviceClick={
            devicesActive && !confinedToWorklist
              ? () => openBlockPanel(BLOCK_ACTOR_DEVICE, { toggle: true })
              : undefined
          }
          // TODO(PR 2): replace these hardcoded `true`s with the real
          // permission checks from useAuthStore once the permission API
          // is wired (Manage_Patient_Appointments + Doctor_Calender).
          // Defaulting to true at the call site keeps the inline-left
          // panel reachable during development while the real auth
          // gates land in PR 2 — the COMPONENT defaults are still
          // fail-closed (false), per Samer §1.
          // Devices tab: the practitioner flows leave the Create menu; the
          // menu offers "Book a device" instead (onBookDeviceClick above).
          // Create is withheld from a confined role for exactly the reason
          // Book Device is: creating an appointment or blocking a
          // practitioner's availability is a front-desk action, not the job
          // of the person running the machine — and more front-desk than
          // booking a device, not less. Missing it the first time left the
          // confined toolbar with a Create button whose menu still offered
          // New Appointment and Block Availability, which is a plain FAIL
          // against "one screen and nothing else".
          //
          // Gated HERE and not in the toolbar because
          // `showCreateButton = canCreateAppointment || canBlockAvailability
          // || !!onBookDeviceClick` — the button disappears only when all
          // three inputs are false, so every input has to be gated at its
          // source. All three now are.
          //
          // Chrome, not a boundary: the appointment endpoints enforce no
          // role (PROD-2388).
          canCreateAppointment={!devicesActive && !confinedToWorklist}
          canBlockAvailability={!devicesActive && !confinedToWorklist}
          fuRequestCount={fuRequestCount}
          specialties={specialties}
          selectedSpecialties={selectedSpecialties}
          onSpecialtyToggle={toggleSpecialty}
          onSpecialtyClear={clearSpecialties}
          onProvidersSelectAll={() =>
            setSelectedProviders(providers.map((p) => p.id))
          }
          onProvidersUnselectAll={clearProviders}
          allProvidersSelected={
            providers.length > 0 &&
            providers.every((p) => selectedProviderIds.includes(p.id))
          }
          noProvidersSelected={selectedProviderIds.length === 0}
        />

        {/* Calendar tab content. Other tabs (E.Requests / Walk In / FU
            Requests) render their own content below. The toolbar
            already updates `activeTab` state when a pill is clicked;
            this guard ensures we only render the calendar surface
            when calendar is the active view.
            PROD-847 PR-1 — FU Requests tab now has real content. */}
        {/* 4. Calendar grid — relative wrapper for the floating filter */}
        <div className="relative flex-1 min-h-0 flex flex-col">
          {activeTab === 'calendar' && (
          <FloatingFilterPill
            waitingRoomCount={listWaitingRoom}
            noShowCount={listNoShow}
            cancelledCount={listCancelled}
            statusOptions={statusLegend}
            organizationLocations={devicesActive ? [] : organizationLocations}
          />
          )}
            {/* Practitioner filter panel: right under the date row, over the strip and the grid, the top layer of the calendar
                (Antonin, 15 Oct: "fix it with highest z index, below the bar where the date, switches are on"). It used to sit
                inside the strip's wrapper, anchored below this whole area and clipped off screen. */}
          {integratedFilters && practitionerPanelOpen && (
            <PractitionerFilterPanel
              onClose={closePractitionerPanel}
              specialties={
                <SpecialtyFilter specialties={specialties} selected={selectedSpecialties} onToggle={toggleSpecialty} onClear={clearSpecialties} />
              }
              bulk={
                <>
                  <button type="button" disabled={providers.length > 0 && providers.every((p) => selectedProviderIds.includes(p.id))}
                    onClick={() => setSelectedProviders(providers.map((p) => p.id))}
                    className="rounded-md px-2 py-1 text-[length:var(--type-body-size)] font-medium text-brand-primary hover:bg-blue-100 disabled:cursor-not-allowed disabled:text-text-hint disabled:hover:bg-transparent">Select all</button>
                  <button type="button" disabled={selectedProviderIds.length === 0} onClick={clearProviders}
                    className="rounded-md px-2 py-1 text-[length:var(--type-body-size)] font-medium text-text-secondary hover:bg-grey-100 disabled:cursor-not-allowed disabled:text-text-hint disabled:hover:bg-transparent">Unselect all</button>
                </>
              }
            />
          )}
          {/* On the Calendar tab this pane never scrolls: the strip stays, and the view's own body is the one scroller
              (showcase-bridge `[data-cal-scroll]`, Antonin 15 Oct). The other tabs keep scrolling here. */}
          <div className={activeTab === 'calendar' ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : 'flex-1 overflow-auto scrollbar-hide'}>

        {activeTab === 'calendar' && (
          <>
        {/* One hairline under the practitioners (Antonin, 15 Oct: "hairline not thick"): the strip's own bottom border is
            dropped here, it stacked with this one into 2 px. */}
        <div className="shrink-0 bg-surface-card border-b border-border-default [&_[data-provider-strip]]:border-b-0 [&_[data-provider-strip]]:pr-[var(--cal-filter-lane)]">

        {/* Provider strip — collapsed/expanded controlled by the
            chevron in the sub-toolbar below. BOTH tabs draw it (PROD-2184,
            Andrew 2026-09-13: "the calendar of devices should be like the
            practitioner one with slots, appointment profiles, mainly the
            cards in the top"). Only the DATA SOURCE changes: the Devices tab
            feeds it `devicesTab.cards`, built by
            `devicesFromDeviceCalendarSlots` onto the same ProviderSummary
            shape, so no component here needed touching. A device card carries
            no photo and no licence issue, which is what stops the card
            drawing an avatar and a missing-licence badge on a machine. */}
        {/* The practitioners stay a row under the Practitioners / Devices selector (Antonin, 13 Oct); only their types
            (specialties) live behind the Practitioners button. */}
        {/* The Practitioners / Devices switch lives once, in the date row above (15 Oct); the strip no longer repeats it. */}
        <ProviderStrip
          providers={devicesActive ? (devicesTab?.cards ?? []) : providers}
          // Device cards have no checkbox: the provider filter narrows the
          // practitioner grid, while the Devices tab narrows COLUMNS by
          // subtype. Omitting both props is what hides the ✓ (ProviderCard
          // renders it only when `onToggleSelect` is supplied).
          selectedProviderIds={devicesActive ? undefined : selectedProviderIds}
          onToggleProvider={devicesActive ? undefined : toggleProvider}
          // The name link opens PROVIDER settings; a device has none.
          onProviderNameClick={devicesActive ? undefined : onProviderNameClick}
          isExpanded={providerListExpanded}
          loading={devicesActive ? (devicesTab?.isLoading ?? false) : providersLoading}
          onRetrySlots={devicesActive ? undefined : onRetryProviderSlots}
          slotsRetrying={devicesActive ? false : providerSlotsRetrying}
          onCanExpandChange={setProviderListCanExpand}
          // PROD-829 — quick-create paths A + B (slot popover + Next
          // inline slot). Slot present → CreateAppointmentPanel opens
          // with start/end pre-filled.
          // Devices book through the existing Book-a-device panel instead:
          // a device booking has no practitioner (PROD-2007), so it must not
          // enter the practitioner quick-create path.
          onSelectSlot={(providerId, slot, profile) =>
            devicesActive
              ? openBookDevice(providerId, slot.start ?? null)
              : handleQuickCreate(providerId, profile, slot)
          }
          // PROD-829 — quick-create path C (profile chip popover →
          // pick a profile). Slot is null → panel opens with profile
          // pre-filled; pre-book hook surfaces the day's slots so the
          // slot picker can populate.
          onSelectProfile={(providerId, _group, profile) =>
            devicesActive
              ? openBookDevice(providerId, null)
              : handleQuickCreate(providerId, profile, null)
          }
          // Hover card "Book: In clinic / Video / Home": provider + location, the profile left for the user to pick.
          onBookLocation={(providerId, _group, profile) =>
            devicesActive
              ? openBookDevice(providerId, null)
              : handleQuickCreate(providerId, profile, null, { locationOnly: true })
          }
        />
        </div>{/* sticky wrapper */}


          {devicesActive && devicesTab ? (
            // Devices tab (PROD-2007): one column per device (Day) or the
            // week grid with device bookings (Week). Never mixed with
            // practitioners.
            calendarView === 'Week' ? (
              <WeekCalendar
                days={days}
                appointments={devicesTab.weekAppointments}
                isLoading={devicesTab.isLoading}
                hours={devicesTab.hours.length > 0 ? devicesTab.hours : undefined}
                onAppointmentClick={handleAppointmentClick}
              />
            ) : (
              <ByDeviceCalendar
                devices={devicesTab.columns}
                rowsByDevice={devicesTab.rowsByDevice}
                availabilityByDevice={devicesTab.availabilityByDevice}
                hours={devicesTab.hours.length > 0 ? devicesTab.hours : undefined}
                selectedDate={selectedDate}
                isLoading={devicesTab.isLoading}
                filtered={devicesTab.selectedSubtypes.length > 0}
                onAppointmentClick={handleAppointmentClick}
                onBookDevice={(deviceId, startISO) => openBookDevice(deviceId, startISO ?? null)}
                onAppointmentDrop={devicesTab.onDeviceAppointmentDrop}
              />
            )
          ) : listView ? (
            <AppointmentListView
              appointments={listAppointments}
              waitingRoom={listWaitingRoom}
              noShow={listNoShow}
              cancelled={listCancelled}
              currentTime={listCurrentTime}
              onAppointmentClick={handleAppointmentClick}
            />
          ) : calendarView === 'Day' ? (
            <DayListCalendar
              rows={dayListRows}
              hours={dayListHours}
              currentHour={dayListCurrentHour}
              selectedDateISO={selectedDate.toISOString()}
              onAppointmentClick={handleAppointmentClick}
              onAppointmentAction={handleRowAction}
              // Scheduling-AI ghost drafts (empty when the flag is off or
              // no draft is active — flag-gated in the useMemo above).
              ghostRows={aiGhostRows}
              isLoading={providersLoading || dayListIsLoading}
            />
          ) : calendarView === 'Doctor' ? (
            <ByDoctorCalendar
              doctors={
                selectedProviderIds.length > 0
                  ? providers.filter((p) => selectedProviderIds.includes(p.id))
                  : providers
              }
              rowsByDoctor={groupRowsByDoctor(dayListRows)}
              hours={dayListHours.length > 0 ? dayListHours : undefined}
              selectedDate={selectedDate}
              onAppointmentClick={handleAppointmentClick}
              onSlotDoubleClick={handleGridQuickCreate}
              onAppointmentDrop={onByDoctorAppointmentDrop}
              isLoading={providersLoading}
            />
          ) : calendarView === 'Room' ? (
            <ByRoomCalendar
              rooms={roomColumns.length > 0 ? roomColumns : undefined}
              rowsByRoom={groupRowsByRoom(dayListRows)}
              hours={dayListHours.length > 0 ? dayListHours : undefined}
              selectedDate={selectedDate}
              isLoading={providersLoading || dayListIsLoading}
              onAppointmentClick={handleAppointmentClick}
            />
          ) : calendarView === 'Month' ? (
            <MonthCalendar
              days={monthGrid}
              monthLabel={monthLabel}
              isLoading={monthIsLoading}
              isError={monthIsError}
              onEventClick={(event) => {
                // Block entries open the BlockAvailabilityPanel in edit mode
                // (same routing as a block tile/row click — Flutter `onSelect`
                // sends blocks to the block edit flow). Patient appointments
                // open the shared ViewAppointmentPopup via the page handler.
                if (event.isBlock) {
                  if (!canEditBlock) {
                    toast.error('You are not authorized to edit blocked availability.');
                    return;
                  }
                  // PROD-2515: this path set no actor kind at all, so a block
                  // opened from the month grid inherited whichever kind was
                  // last used. Same evidence as the day/week path — the tab the
                  // user is on — and since PROD-2512 the kind picks the
                  // hydration route, so a wrong one is a 400.
                  openBlockPanel(devicesActive ? BLOCK_ACTOR_DEVICE : BLOCK_ACTOR_PRACTITIONER, {
                    blockId: event.id,
                  });
                  return;
                }
                onMonthEventClick?.(event);
              }}
              onDayClick={(iso) => {
                onMonthDayClick?.(iso);
                // After picking a day in the month grid, drop back into
                // Day view so the user can act on appointments. Mirrors
                // Flutter calendar_by_month_widget.dart navigation.
                setCalendarView('Day');
              }}
            />
          ) : (
            <WeekCalendar
              days={days}
              appointments={appointments}
              // PROD-963: pass the dynamic hour gutter so early-morning
              // and after-hours bookings render in the visible range.
              // dayListHours is computed from the week's filtered
              // appointment set + practiceWorkingHours by hoursForDayList.
              hours={dayListHours.length > 0 ? dayListHours : undefined}
              onAppointmentClick={handleAppointmentClick}
              isLoading={providersLoading || dayListIsLoading}
              onSlotClick={() => {
                // Manual entry too — same suspension as toolbar Create
                // (Codex P1 on e6808d97: Week-view slot click could still
                // be clobbered by a late parse).
                startManualFlow();
                setActivePanel('appointment');
              }}
              onAppointmentDrop={onAppointmentDrop}
            />
          )}
          </>
        )}

        {/* FU Requests tab (PROD-847 PR-1).
            PROD-920: passes `onCreateFollowUp` so the row kebab's
            "Create Follow Up" item opens `CreateAppointmentPanel`
            pre-populated with the row's patient + provider. */}
        {activeTab === 'fuRequests' && (
          <FollowUpRequestsTab onCreateFollowUp={handleFuCreateFollowUp} />
        )}

        {/* Order Requests — Phlebotomist-only Lab linkage tab.
            Belt-and-suspenders (PR #295 Samer review §4):
            1. Tab pill is filtered OUT of the toolbar's `TABS` array
               (CalendarToolbar.tsx `ALL_TABS.filter(...)`) — the pill
               element doesn't exist in the DOM for non-Phlebotomists.
            2. THIS LINE: HARD UNMOUNT. The `&&` short-circuit means
               when the gate is false, the JSX evaluates to `false`,
               which React skips entirely — `<OrderRequestsTab />` is
               NEVER mounted, `useLabOrdersList` is NEVER called, no
               network request fires, no data lands in the cache, no
               PHI is exposed. This is NOT a CSS `display: none` —
               the component instance does not exist.
            3. `showLabLinkage` is flag AND role — `VITE_LAB_LINKAGE_ENABLED`
               ANDed with Phlebotomist-OR-OrgAdmin (see its definition
               above). Corrected 2026-09-17: this line used to claim the
               feature-flag gate had been removed and that visibility was
               role-only, which the code three lines above the gate has
               contradicted ever since Samer's PR #529 review #5 put the
               flag back. */}
        {activeTab === 'orderRequests' && showLabLinkage && <OrderRequestsTab />}

        {/* Referrals (PROD-1395, `?x=referrals`): hard unmount when the switch is off, like Order Requests. */}
        {activeTab === 'referrals' && referralsEnabled && <ReferralsTab location={referralsLocation} />}

        {/* Device Sessions — the Technician Worklist (PROD-2007).
            Same two-layer containment as Order Requests above, and for the
            same reason:
            1. The pill is filtered OUT of the toolbar's `TABS` array
               (`showTechnicianWorklistTab`), so for a user who fails the
               gate there is no element in the DOM to find or re-enable.
            2. THIS LINE: HARD UNMOUNT, and the `&&` is load-bearing rather
               than tidy. When `showTechnicianWorklist` is false the JSX
               evaluates to `false`, which React skips outright —
               `<TechnicianWorklistTab />` is never instantiated, so
               `useTechnicianSessions` never runs, no request fires, and no
               patient row lands in the React Query cache. Swapping this for
               a `hidden` class or a CSS `display: none` would keep every one
               of those, and would hand a devtools-literate user the data by
               deleting one attribute.
            Keeping the gate ANDed here (rather than trusting the missing
            pill) also covers the live case the pill cannot: `activeTab` is
            frame-local state, so a user already sitting on this tab when the
            flag is pulled or their role changes is unmounted on the next
            render instead of keeping the surface.
            What this is NOT: a security boundary. The appointment endpoints
            behind the worklist do not check for a Technician role, so the
            same data is reachable by any authenticated caller who goes
            straight at the API. This scopes the UI and the cache; it does
            not protect the records. See the gate's definition above. */}
        {activeTab === 'technicianWorklist' && showTechnicianWorklist && <TechnicianWorklistTab />}
          </div>
        </div>
      </div>

      {railLayout === 'wide' && bookingRails}

      {/* Cancel-appointment dialog — recurring rows show the scope picker
          (this only / following / all) before the reason picker. */}
      <CancelAppointmentDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
        target={
          cancelTarget
            ? {
                appointmentId: cancelTarget.id,
                patientId: cancelTarget.patientId,
                practitionerId: cancelTarget.practitionerId,
                startISO: cancelTarget.startISO,
                recurringId: cancelTarget.recurringId,
              }
            : null
        }
      />

      {/* Reschedule fallback dialog — focused date+time picker, keeps duration.
          Only reached when a row lacks the ids the inline panel needs to
          PreLoad (near-impossible for a real appointment row). Reschedules the
          single occurrence via the dialog's internal `useRescheduleAppointment`
          (PUT /Appointment/Reschedule) — no recurring-scope prompt, matching
          Flutter (the scope picker lives only in the series edit menu, never on
          the reschedule action). */}
      <RescheduleAppointmentDialog
        open={rescheduleTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRescheduleTarget(null);
        }}
        target={
          rescheduleTarget
            ? {
                appointmentId: rescheduleTarget.id,
                startISO: rescheduleTarget.startISO,
                endISO: rescheduleTarget.endISO,
              }
            : null
        }
      />

      {/* Add Note dialog — internal note on the appointment.comment field. */}
      <AddNoteDialog
        open={noteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setNoteTarget(null);
        }}
        target={
          noteTarget
            ? {
                appointmentId: noteTarget.id,
                initialNote: noteTarget.note,
              }
            : null
        }
      />

      {/* View PVS (Post-Visit Summary) — opened from the row overflow menu
          (PROD-1651). Same dialog the view-appointment popup footer uses. */}
      <PostVisitSummaryDialog
        open={pvsTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPvsTarget(null);
        }}
        appointmentId={pvsTarget?.id ?? ''}
      />

      {/* Experiment (Antonin, 14 Oct): central booking modal for follow-up
          flows. Only rendered when MODAL_BOOKING is true. The inline side
          panel stays for calendar-driven creation. */}
      {MODAL_BOOKING && modalBooking && (
        <BridgeCreateAppointmentModal
          open
          onOpenChange={(open) => {
            if (!open) setModalBooking(null);
          }}
          organizationId={organizationId}
          practiceId={practiceId}
          userId={userId}
          onSearchPatients={onSearchPatients}
          initialValues={modalBooking.initialValues}
        />
      )}
    </div>
  );
}

/** Leaving Order Requests by its tab: the address stops asking for it, so a reload opens the tab picked now. */
function dropOrderRequestsTab(tab: SchedulingTab, pathname: string, search: string, navigate: NavigateFunction) {
  if (tab === 'orderRequests' || !opensOrderRequests(search)) return;
  const p = new URLSearchParams(search);
  p.delete(ORDER_REQUESTS_TAB_PARAM);
  const qs = p.toString();
  navigate(`${pathname}${qs ? `?${qs}` : ''}`, { replace: true });
}
